/**
 * Yutrients BYO — client controller for /pages/build-your-own.
 *
 * State machine:
 *   name → loading → picker
 *
 * Entry points:
 *   - Cold (no query, no sessionStorage): name → loading (catalogue fetch) → picker
 *   - Pre-fill (?stack=<id>): loading (catalogue + formula fetch in parallel) → picker
 *   - Saved state (sessionStorage from <7 days ago): loading → picker, restore state
 *
 * Backend (via /apps/yutrients/* App Proxy):
 *   - GET /catalogue → ingredients[] (cached 5min via ETag)
 *   - GET /formula/:id → pre-fill payload
 *   - POST /formula/submit → creates variant, returns formula_id + variant_id
 *
 * Pricing maths runs client-side over the catalogue's derived
 * cost_per_mg_gbp + dispensing_fee_gbp fields. See pricing-model.md.
 *
 * Spec: docs/01-storefront/build-your-own-page.md
 */

(function () {
  'use strict';

  // ─── constants ─────────────────────────────────────────────────────

  const STORAGE_KEY = 'yutrients_byo_state';
  const STORAGE_TTL_DAYS = 7;
  const STORAGE_SCHEMA_VERSION = 1;
  /**
   * Minimum ingredients before submit CTAs unlock. Was 3 per the original
   * spec; relaxed to 1 (designer iter-1) so customers can mint a single-
   * ingredient stack if they want — the price floor (£50/month) still
   * applies so commercially nothing breaks.
   *
   * NOTE: the server's propose_stack validator still requires min 3 from
   * AI-generated stacks. That's intentional — the AI shouldn't ship
   * 1-ingredient blends. Customer-driven BYO is different: their choice.
   */
  const MIN_STACK = 1;
  // 2026-06-02: raised from 25 to 100 (anti-abuse only; no practical cap).
  // The max-stack tooltip is now effectively dead UI in normal use — kept
  // wired up for the edge case.
  const MAX_STACK = 100;
  // Pricing-model defaults. These are overridden at runtime from the catalogue
  // payload's `pricing` block (set in the admin Ingredients → Pricing tab), so a
  // floor/discount change in admin flows to the live preview. The fallbacks here
  // keep the page working if the payload omits pricing (older formulator).
  // 2026-08-12 model: the equation prices the SUBSCRIPTION; one-time = sub × 1.25.
  // These are overridden by the catalogue payload's `pricing` block; the fallbacks
  // must match the live model so a missing payload can't under-display the price.
  let VARIANT_FLOOR = 50;
  /* The tin-name cap. 23 Sep 2026: a 46-character name reached the laser
     and did not fit. This input said 50 (lifted from 15 on 2 Sep so base
     defaults would fit, before bases had short tin_names) and the server
     said 60, so nothing said no. 22 is what the tin preview renders at full
     size; formulator/src/formula/name.ts holds the same number and migration
     168 enforces it in the database. Change it there first. */
  const TIN_NAME_MAX = 22;
  let SUB_DISCOUNT = 1.0;
  let BUNDLE_DISCOUNT = 1.0;
  // 90-day monthly floor — LOCKSTEP with formulator pricing.ts
  // PREPAID_MONTHLY_FLOOR_GBP (founder 2026-08-13: "£45 minimum for 90
  // days"). Overridable via catalogue params if the server ever sends one.
  let PREPAID_FLOOR = 45;
  // Bundle-world switch (served by the catalogue payload): flips the
  // preview to the 30-day-sub basis, kills the legacy 90-day framing and
  // the £45 bar, and shows the subscribe/one-time selector.
  let BUNDLES_MODE = false;
  // Cost-plus model (2026-07-18, 63% margin). The catalogue payload overrides
  // these from the live pricing config; defaults match the formulator constants
  // so a missing payload still prices sanely. BASE = £11 fixed per-order pool
  // (fulfilment £7 + £2 pre + £2 post production labour) ÷ 0.37; taper is gone
  // (0). The equation prices the SUBSCRIPTION; the one-time price is sub ×
  // 1.25 (= ÷0.8, 2026-08-12 offer ladder — mirrors the 30-day selling plan's
  // 20% percentage discount). See 0006-pricing-model.md.
  // 28 Sep 2026: the fallback is the live bundle-world manufacturing & lab
  // fee (£15, formulator pricing.ts dispensingPostMarginGbp) — the builder
  // now shows the fee as a line from the first paint, before the catalogue
  // payload lands, so the fallback must be the figure the customer pays.
  let PRICE_BASE = 15;
  let PRICE_TAPER_AFTER = 15;
  let PRICE_TAPER_GBP = 0;
  let ONE_TIME_MULTIPLIER = 1.25;

  /**
   * Filter group config — drives both desktop sidebar and mobile drawer.
   *
   * Order (designer iter-5): Categories → Popularity → Used for.
   * The legacy Stack Type radio (Basic Top-50 / Advanced Full List)
   * was dropped — designer wants ALL ingredients shown unfiltered by
   * tier. The customer narrows via category + goal instead.
   */
  const FILTER_GROUPS = [
    {
      // Sort order. The page DEFAULTS to Most Popular (founder 2026-08-15:
      // ~99% of landers never touch a filter, so the default IS the
      // experience — bestsellers first reads as a menu of proven picks now
      // the cards carry prices). Alphabetical keeps the letter dividers +
      // the mobile A–Z jump rail for the knows-what-they-want visitor.
      id: 'sort',
      label: 'Sort by',
      type: 'radio',
      options: [
        { value: 'popular', label: 'Most Popular' },
        { value: 'alpha', label: 'Alphabetical (A–Z)' },
      ],
    },
    {
      id: 'category',
      label: 'Categories',
      type: 'checkbox',
      // Mirrors the yutrients.ingredient_category enum values seeded for the
      // 211-ingredient catalogue (catalogue rebuild 2026-06-17).
      options: [
        { value: 'vitamin', label: 'Vitamins' },
        { value: 'mineral', label: 'Minerals' },
        { value: 'amino_acid', label: 'Amino Acids' },
        { value: 'botanical', label: 'Botanicals & Adaptogens' },
        { value: 'mushroom', label: 'Mushrooms' },
        { value: 'omega', label: 'Omegas & Fatty Acids' },
        { value: 'probiotic', label: 'Probiotics & Digestive' },
        { value: 'longevity', label: 'Longevity & Other' },
      ],
    },
    {
      id: 'popularity',
      label: 'Popularity',
      type: 'checkbox',
      options: [
        { value: 'most_popular', label: 'Most Popular' },
        { value: 'trending', label: 'Trending' },
        { value: 'frequently_used', label: 'Frequently Used' },
        { value: 'beginner_friendly', label: 'Beginner Friendly' },
        { value: 'advanced_only', label: 'Advanced Only' },
      ],
    },
  ];

  // ─── sessionStorage helpers ────────────────────────────────────────

  const store = (function () {
    let mem = null;
    let useSession = true;
    try {
      const probe = '__yt_probe__';
      window.sessionStorage.setItem(probe, '1');
      window.sessionStorage.removeItem(probe);
    } catch (_e) { useSession = false; }
    return {
      read() {
        try {
          const raw = useSession ? window.sessionStorage.getItem(STORAGE_KEY) : mem;
          if (!raw) return null;
          const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (parsed.schema_version !== STORAGE_SCHEMA_VERSION) return null;
          const ageDays = (Date.now() - new Date(parsed.saved_at).getTime()) / 86400000;
          if (ageDays > STORAGE_TTL_DAYS) return null;
          return parsed;
        } catch (_e) { return null; }
      },
      write(state) {
        const payload = JSON.stringify({ ...state, schema_version: STORAGE_SCHEMA_VERSION, saved_at: new Date().toISOString() });
        if (useSession) {
          try { window.sessionStorage.setItem(STORAGE_KEY, payload); } catch (_e) { mem = payload; }
        } else { mem = payload; }
      },
      clear() {
        if (useSession) {
          try { window.sessionStorage.removeItem(STORAGE_KEY); } catch (_e) {}
        }
        mem = null;
      },
    };
  })();

  // ─── utilities ─────────────────────────────────────────────────────

  function round2(n) { return Math.round(n * 100) / 100; }
  // Doses are stored in mg with the µg ingredients three places in (25 µg =
  // 0.025 mg; chromium, selenium, iodine and molybdenum step in 0.025). A
  // dose rounded to two places lands OFF the grid — 0.075 became 0.08, 0.025
  // became 0.03 — and the server refused the blend with a 422 the customer
  // read as "something went wrong" (Pete Davies, 24 Sep 2026). Six places,
  // the same precision mgToDisplay and the server's own grid use.
  function roundDose(n) { return Math.round(n * 1e6) / 1e6; }

  /**
   * iter-25: round DOWN to the nearest multiple of `step` starting from `min`.
   * Used when the customer types a dose. Rounds-down so a customer typing
   * 437 with min=0/step=100 lands on 400 — never 500 — keeping them safely
   * AT or below their intended ceiling. Anchoring to `min` is important
   * for step values that don't divide into the absolute number cleanly
   * (e.g. min=15, step=5 → valid doses are 15, 20, 25 etc, so 23 → 20).
   */
  function floorToStep(value, min, step) {
    if (!isFinite(value)) return min;
    if (step <= 0) return Math.max(min, value);
    // A whisker of tolerance before the floor: (0.075 − 0.025) / 0.025 is
    // 1.9999999999999998 in floating point, and a typed 75 µg of chromium
    // used to floor to 50 µg (25 Sep 2026). The server's grid check allows
    // the same 1e-6.
    const stepsFromMin = Math.floor((value - min) / step + 1e-6);
    if (stepsFromMin < 0) return min;
    return roundDose(min + stepsFromMin * step);
  }

  /**
   * The INTERNAL (hard) max for an ingredient — iter-25b's real ceiling
   * (2026-08-27): `hard_max_dose_mg` from the catalogue when set above the
   * customer-facing max, else the customer max itself. Replaces the old
   * 1000×default placeholder: the sellable dose menu only exists up to this
   * number, so the builder must never let a dose past it.
   */
  function hardMaxOf(ing) {
    const soft = Number(ing.max_dose_mg);
    const hard = Number(ing.hard_max_dose_mg);
    return isFinite(hard) && hard > soft ? hard : soft;
  }

  /**
   * A dose the server will accept for this ingredient: on the grid (min
   * plus whole steps), never above the hard ceiling, never below min.
   * Founder 2026-09-24 — "clicked next and it just goes back to the
   * formula builder screen": a stack restored from storage, or prefilled
   * from a chat formula (333 mg where the grid is 250s), was submitted
   * with its saved amounts untouched; the server refused it as off-grid
   * (422) and the customer was bounced back with an error they never saw.
   * Every amount that enters the stack from outside the stepper goes
   * through here first, so what is submitted is always what the stepper
   * itself could have produced.
   */
  function snapToGrid(ing, mg) {
    const min = Number(ing.min_dose_mg);
    const step = Number(ing.dose_step_mg);
    const hardMax = hardMaxOf(ing);
    let next = Number(mg);
    if (!isFinite(next) || next < min) next = min;
    if (isFinite(hardMax) && next > hardMax) next = hardMax;
    if (step > 0) {
      next = floorToStep(next, min, step);
      if (isFinite(hardMax) && next > hardMax) next = floorToStep(hardMax, min, step);
    }
    return next < min ? min : next;
  }

  function debounce(fn, ms) {
    let t = null;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  /* ─── Ingredient search ──────────────────────────────────────────────────
   *
   * Ranked, typo-tolerant lookup — the same engine the admin catalogue uses
   * (admin/lib/search/ingredient-search.ts), ported here because the shop's
   * search was plain substring matching: "gingar" found nothing, and a
   * customer who can't find Ginger doesn't buy Ginger (founder 2026-07-25).
   *
   * Hand-rolled rather than a library: ~200 rows means the win is match
   * QUALITY, not index scale, and this adds no bytes to fetch. Everything runs
   * per keystroke over a prebuilt index.
   *
   * Match tiers per query token, strongest first: exact field → field prefix →
   * word prefix → substring → one-typo word (bounded Damerau-Levenshtein: 1
   * edit at 4+ chars, 2 at 8+) → in-order subsequence (so "ashw" and "mgnesium"
   * still land). Fields are weighted name > alias > tagline > category > tags.
   * Multi-word queries AND together; the score sums each token's best field.
   */

  /** Lowercase, strip diacritics, collapse everything non-alphanumeric to spaces. */
  function searchNormalise(s) {
    return String(s || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function searchField(raw, weight) {
    const text = searchNormalise(raw);
    if (!text) return null;
    return { text: text, words: text.split(' '), weight: weight };
  }

  function buildSearchIndex(items) {
    return items.map(function (it) {
      const fields = [];
      const push = (f) => { if (f) fields.push(f); };
      push(searchField(it.name, 100));
      (it.aliases || []).forEach((a) => push(searchField(a, 85)));
      push(searchField(it.tagline, 55));
      push(searchField(it.category, 40));
      (it.discovery_tags || []).forEach((t) => push(searchField(t, 35)));
      (it.goal_tags || []).forEach((t) => push(searchField(t, 35)));
      return { id: it.id, fields: fields };
    });
  }

  /** Bounded Damerau-Levenshtein: true when distance ≤ maxEdits. */
  function withinEdits(a, b, maxEdits) {
    if (maxEdits <= 0) return a === b;
    if (Math.abs(a.length - b.length) > maxEdits) return false;
    const m = a.length;
    const n = b.length;
    let prevPrev = [];
    let prev = [];
    for (let j = 0; j <= n; j++) prev.push(j);
    for (let i = 1; i <= m; i++) {
      const cur = [i];
      let rowMin = i;
      for (let j = 1; j <= n; j++) {
        const cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (
          i > 1 && j > 1 &&
          a.charAt(i - 1) === b.charAt(j - 2) &&
          a.charAt(i - 2) === b.charAt(j - 1)
        ) {
          v = Math.min(v, prevPrev[j - 2] + 1); // transposition
        }
        cur.push(v);
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > maxEdits) return false; // whole row over budget → bail
      prevPrev = prev;
      prev = cur;
    }
    return prev[n] <= maxEdits;
  }

  function typoBudget(token) {
    if (token.length >= 8) return 2;
    if (token.length >= 4) return 1;
    return 0;
  }

  /** In-order subsequence with an adjacency bonus; 0 when not a subsequence. */
  function subsequenceScore(token, text) {
    let ti = 0;
    let adjacent = 0;
    let last = -2;
    for (let i = 0; i < text.length && ti < token.length; i++) {
      if (text[i] === token[ti]) {
        if (i === last + 1) adjacent++;
        last = i;
        ti++;
      }
    }
    if (ti < token.length) return 0;
    return 1 + adjacent / Math.max(1, token.length - 1);
  }

  /** Tier score of one token against one field (0 = no match). */
  function tokenFieldScore(token, f) {
    if (f.text === token) return 10;
    if (f.text.startsWith(token)) return 6;
    for (const w of f.words) if (w.startsWith(token)) return 4.5;
    if (f.text.includes(token)) return 3;
    const budget = typoBudget(token);
    if (budget > 0) {
      for (const w of f.words) if (withinEdits(token, w, budget)) return 2.5;
    }
    // Subsequence only for 3+ chars — shorter tokens match everything.
    if (token.length >= 3) {
      const s = subsequenceScore(token, f.text);
      if (s > 0) return Math.min(2, s);
    }
    return 0;
  }

  /** → Map(ingredient id → score). Every token must match somewhere (AND). */
  function searchScores(index, query) {
    const tokens = searchNormalise(query).split(' ').filter(Boolean);
    const out = new Map();
    if (!tokens.length) return out;
    for (const entry of index) {
      let total = 0;
      let ok = true;
      for (const token of tokens) {
        let best = 0;
        for (const f of entry.fields) {
          const tier = tokenFieldScore(token, f);
          if (tier <= 0) continue;
          const s = tier * f.weight;
          if (s > best) best = s;
        }
        if (best <= 0) { ok = false; break; }
        total += best;
      }
      if (ok) out.set(entry.id, total);
    }
    return out;
  }

  /** Stable JSON serialise for the canonical stack hash. */
  function canonicaliseStack(ingredients) {
    return ingredients
      .map(i => ({ id: i.ingredient_id, mg: Number(i.amount_mg) }))
      .sort((a, b) => a.id.localeCompare(b.id));
  }

  async function hashStack(ingredients) {
    const canonical = JSON.stringify(canonicaliseStack(ingredients));
    const buf = new TextEncoder().encode(canonical);
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  function uuidv4() {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  function formatGBP(n) {
    return '£' + n.toFixed(2);
  }

  /**
   * fetch() with a hard timeout. Without one, a stalled connection leaves the
   * request pending forever — on the submit path that means the "Making your
   * formula" bar sits frozen with no error and no retry, and the customer just
   * leaves. AbortController fires after `ms`; we translate the resulting
   * AbortError into a friendly, user-facing message so the caller's catch shows
   * the retry affordance rather than a raw DOMException. `timeoutMessage` lets
   * each call site phrase the failure in its own words.
   */
  async function fetchWithTimeout(resource, options, ms, timeoutMessage) {
    // Guard: older browsers without AbortController still work, just untimed.
    if (typeof AbortController === 'undefined') {
      return fetch(resource, options);
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    try {
      return await fetch(resource, { ...(options || {}), signal: controller.signal });
    } catch (e) {
      if (e && e.name === 'AbortError') {
        throw new Error(timeoutMessage || 'This is taking longer than expected — please try again.');
      }
      throw e;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Parse a dose string like "200mg", "25 mcg", "3g", "1cap",
   * "25bncfu", "500" into a milligram amount. Returns
   * `fallbackMg` (the catalogue ingredient's default) when the
   * string can't be confidently parsed.
   *
   *   mg  → ×1
   *   mcg → ÷1000
   *   g   → ×1000
   *   bare number → assume mg
   *   anything else → fallback
   */
  function parseDoseToMg(raw, fallbackMg) {
    if (raw == null) return fallbackMg;
    const cleaned = String(raw).replace(/\s+/g, '').toLowerCase();
    const m = cleaned.match(/^([0-9]+(?:\.[0-9]+)?)([a-z]*)$/);
    if (!m) return fallbackMg;
    const n = parseFloat(m[1]);
    if (!Number.isFinite(n)) return fallbackMg;
    const unit = m[2];
    if (unit === '' || unit === 'mg')  return n;
    if (unit === 'mcg' || unit === 'ug') return n / 1000;
    if (unit === 'g')  return n * 1000;
    // Probiotics are dosed in billions of CFU and stored as that number
    // ("2bncfu" → 2), exactly like the catalogue row.
    if (unit === 'bncfu' || unit === 'bn') return n;
    // cap/cfu/etc. aren't milligrams — fall back to the catalogue
    // default so we at least pre-select the ingredient.
    return fallbackMg;
  }

  /**
   * Unit-conversion factor: how many DISPLAY units there are per 1 mg.
   * The catalogue + stack + pricing are all canonical milligrams; the
   * customer-facing dose is shown in the ingredient's listed `unit`.
   *   mg  → 1        (e.g. 300 mg stays 300)
   *   mcg → 1000     (e.g. 0.025 mg shows as 25 mcg)
   *   g   → 0.001    (e.g. 5000 mg shows as 5 g)
   * Anything unexpected (incl. IU, which is handled per-ingredient on the
   * PDP/label, not in the BYO stepper) falls back to mg so we never
   * mis-scale a dose.
   */
  function unitFactor(unit) {
    const u = String(unit || 'mg').toLowerCase();
    if (u === 'mcg' || u === 'ug') return 1000;
    if (u === 'g') return 0.001;
    // bn_cfu (billion CFU, probiotics): the stored number IS the dose → 1.
    return 1;
  }

  /**
   * What to print after the number: 'mg', 'µg', 'g' or 'bn CFU' (never the
   * raw catalogue token — 'mcg' and 'bn_cfu' are storage names).
   */
  function unitLabel(unit) {
    const u = String(unit || 'mg').toLowerCase();
    if (u === 'mcg' || u === 'ug') return 'µg';
    if (u === 'bn_cfu' || u === 'bncfu' || u === 'bn cfu') return 'bn CFU';
    if (u === 'g') return 'g';
    return 'mg';
  }

  /** unitLabel with a leading space when it is a word ("2 bn CFU", "300mg"). */
  function unitSuffix(unit) {
    const l = unitLabel(unit);
    return l.indexOf(' ') >= 0 ? ' ' + l : l;
  }

  /** mg → display-unit string, trimming float noise (25, not 25.0000001). */
  function mgToDisplay(mg, unit) {
    const v = Number(mg) * unitFactor(unit);
    if (!isFinite(v)) return '0';
    const r = Math.round(v * 1e6) / 1e6;
    return String(r);
  }

  // ─── controller ────────────────────────────────────────────────────

  class BYOController {
    constructor(root) {
      this.root = root;
      this.proxyBase = root.dataset.proxyBase || '/apps/yutrients';

      // DOM refs. Loading is its own section because it covers fetches
      // that hide the whole page. There is no naming screen any more
      // (founder, 28 Sep 2026): every custom tin is printed "My Blend", or
      // the base's own tin name when the blend is built on a base.
      this.loadingSection = document.querySelector('[data-yt-byo-loading]');
      this.pickerSection = document.querySelector('[data-yt-byo-picker]');

      this.copy = {
        minStackTooltip: root.dataset.ytMinStackTooltip || 'Add at least one ingredient to continue.',
        maxStackTooltip: root.dataset.ytMaxStackTooltip || 'Maximum 25 ingredients reached.',
        overMaxHeading: root.dataset.ytOverMaxHeading,
        networkError: root.dataset.ytNetworkError || 'Network blip — please try again.',
        retryLabel: root.dataset.ytRetryLabel || 'Try again',
        rateLimitPrefix: root.dataset.ytRateLimitPrefix || "You're sending requests too quickly. Please give it about",
        // Submit-only loader: a determinate progress bar that runs while
        // the BYO submit chain (formulator → variant-manager → cart-add)
        // is in flight. Different from the shared pearl loader so the
        // customer sees a visible commitment ("Making your formula") and
        // a sense of progress for the 5-8s wait. See showSubmitLoader().
        submitLoaderHeading: root.dataset.ytSubmitLoaderHeading || 'Creating your formula',
        submitLoaderSubline: root.dataset.ytSubmitLoaderSubline || "Hang tight — please don't close this screen.",
      };

      // Recharge 1-month selling-plan id, theme-configured. Empty when
      // Recharge isn't wired up yet — cart-add then sends a plain one-time
      // line which the cart renders at the variant's raw `variant_price`
      // (the floor-based undiscounted one-time figure, NOT the £50 monthly the
      // customer was promised). Set the id and the cart shows the £50.
      // The attribute key contains a digit ("1mo"), so dataset's camelCase
      // mapping can differ between browsers. Read with getAttribute to
      // avoid surprises.
      this.sellingPlan1Mo = (root.getAttribute('data-yt-selling-plan-1mo') || '').trim();
      // Weighing spoons variant id — auto-added to the cart as a one-time add-on
      // (free for subscriptions via a Shopify discount Function).
      this.spoonVariantId = (root.getAttribute('data-yt-spoon-variant-id') || '').trim();

      // One-time debuggability warn — mirrors yt-formula-pdp.js
      // warnIfMissingPlans(). Fires once on construct when the 1-month
      // selling-plan id is blank so it's obvious in the console that
      // ADD TO BASKET will fall back to a one-time line (NOT the default
      // 1-month subscription per docs/07-decisions/0005-recharge-default-sub.md).
      // Never blocks the add-to-cart — it's purely a heads-up.
      this.warnIfMissingPlan();

      // State
      this.catalogue = [];               // PublicIngredient[]
      this.catalogueById = new Map();
      this.stack = [];                   // [{ ingredient_id, amount_mg }]
      this.primaryGoals = [];
      this.preFillHash = null;
      this.formulaIdPrefill = null;
      // ALWAYS-FRESH (2026-06-26): the old in-place-update / no-op-shortcut
      // state (updateInPlaceEligible / existingShopifyVariantId /
      // existingStackHash) is gone — every submit creates a brand-new variant.
      this.filters = {
        // Default landing order is Most Popular (founder 2026-08-15). See
        // FILTER_GROUPS.
        sort: 'popular',
        used_for: new Set(),
        category: new Set(),
        popularity: new Set(),
        search: '',
      };
      this.busy = false;
      // B5 BASE (2026-08-27): the locked base under this blend. Loaded from
      // ?base=<slug> (a base product page's "customise this base" link) or
      // restored from the saved state; null = from scratch, the default
      // landing since 28 Sep 2026. The
      // base is FIXED — customers only ADD on top; its ingredient ids drive
      // the add-on demotion + the duplicate-add warning.
      this.base = null;
      this.baseSlug = null;
      this.baseIngredientIds = new Set();
      this.dupAcknowledged = new Set();
      // iter-25c: per-ingredient "I know it's over the recommended
      // max, stop warning me" acknowledgement. Once the customer
      // dismisses (CONTINUE) or applies (SET TO MAX) the over-soft-max
      // modal for an ingredient, that id lands here and the modal
      // never fires for it again — no matter how many further dose
      // changes (typed or stepped) they make. Removing the ingredient
      // from the stack clears the entry, so re-adding starts fresh
      // at the default dose with a fresh acknowledgement state.
      // Not persisted — a page reload OR a re-add resets the cohort.
      this.overMaxAcknowledged = new Set();

      this.persistDebounced = debounce(() => this.persist(), 250);

      this.init();
    }

    async init() {
      this.bindPicker();

      const params = new URLSearchParams(window.location.search);
      let stackId = params.get('stack');
      const prefillSlugs = params.get('prefill');
      const fromAccount = params.get('from') === 'account';

      // Founder 2026-08-15: Most Popular is the DEFAULT for every arrival
      // (the 2026-07-25 per-arrival split is retired — ?stack= handovers and
      // direct visitors alike land on bestsellers-first; alphabetical stays
      // one tap away in the drawer with its dividers + jump rail).

      // Customer-portal "Edit your stack": the Recharge portal button opens BYO
      // with ?subscription=<recharge_sub_id>&source=portal. In this mode the
      // submit RE-POINTS the existing subscription (Recharge swap) instead of
      // adding a new cart line. Only active when source=portal — non-portal BYO
      // is completely unaffected.
      this.purchaseMode = 'sub';
      this.portalSubscriptionId =
        params.get('source') === 'portal' ? (params.get('subscription') || null) : null;

      // Portal Custom-HTML button (2026-07-10): the widget can't run JS, so it
      // links here with source=portal and NO id — resolve the logged-in
      // customer's single active subscription server-side. Multiple actives or
      // any failure → fall through to a normal blank builder (safe).
      if (params.get('source') === 'portal' && !this.portalSubscriptionId) {
        try {
          const r = await fetchWithTimeout(
            this.proxyBase + '/account/subscriptions',
            { headers: { Accept: 'application/json' }, credentials: 'same-origin' },
            15000,
            'timeout',
          );
          if (r.ok) {
            const d = await r.json();
            const active = ((d && d.subscriptions) || []).filter(
              (sub) => (sub.status || '').toLowerCase() === 'active',
            );
            if (active.length === 1) this.portalSubscriptionId = String(active[0].id);
          }
        } catch (_e) {
          /* stay in normal BYO mode */
        }
      }
      if (this.portalSubscriptionId) this.applyPortalModeCtas();

      // The portal link carries the subscription id, not the formula id — resolve
      // the sub → its current formula so we prefill it (ownership-checked server
      // side). If it can't resolve, fall through to a blank builder.
      if (this.portalSubscriptionId && !stackId) {
        try {
          const r = await fetchWithTimeout(
            this.proxyBase + '/account/subscriptions/' + encodeURIComponent(this.portalSubscriptionId),
            { headers: { Accept: 'application/json' }, credentials: 'same-origin' },
            15000,
            'timeout',
          );
          if (r.ok) {
            const d = await r.json();
            if (d && d.formula_id) stackId = d.formula_id;
          }
        } catch (_e) {
          /* fall through — customer can rebuild from scratch */
        }
      }

      // "Build another" (founder 2026-08-24): the basket's Build-another
      // button links here with ?fresh=1 — wipe the saved draft so the
      // builder starts BLANK instead of resuming the last stack. The param
      // is stripped from the URL so a refresh afterwards keeps the new
      // in-progress stack rather than wiping again.
      if (params.get('fresh') === '1') {
        store.clear();
        try {
          const clean = new URL(window.location.href);
          clean.searchParams.delete('fresh');
          window.history.replaceState({}, '', clean.pathname + (clean.search || '') + clean.hash);
        } catch (_e) { /* cosmetic only */ }
      }
      const savedState = store.read();

      // A ?stack=<id> link re-seeds the builder from the STORED formula —
      // which, after a failed submit and the browser's back button, threw
      // away every change the customer had just made (Pete Davies, 24 Sep
      // 2026: "I have lost all my changes and reverted back to my original
      // formula"). When the saved draft IS an edit of that very formula, the
      // draft wins: it is the newer of the two. A different formula, a
      // ?fresh=1 or no draft still seeds from the link as before.
      const resumeEdit =
        !!stackId && !!savedState && !prefillSlugs &&
        savedState.formula_id_prefill === stackId &&
        Array.isArray(savedState.ingredients);

      if (prefillSlugs) {
        // Pre-fill from the yt-stacks "Start with this formula" CTA on
        // the home page. The query string is
        //   ?prefill=<slug>:<dose>,<slug>:<dose>,...
        // where <slug> matches `catalogue[i].slug` and <dose> is a
        // string like "200mg", "25mcg", "3g", "1cap", "25bncfu".
        // Doses that don't parse to milligrams are dropped (so e.g.
        // "1 cap" is matched as the ingredient but the dose falls
        // back to its `default_dose_mg`).
        await this.runLoadingThen(async () => {
          const cat = await this.fetchCatalogue();
          this.applyCatalogue(cat);
          this.applyPrefillBySlugs(prefillSlugs);
          this.showPicker();
        });
      } else if (stackId && !resumeEdit) {
        // Pre-fill from chat / image / saved formula. Name comes with
        // the formula payload — no modal needed. We DO need the loading
        // state because the formula fetch takes a noticeable moment and
        // we don't want to flash an empty picker.
        await this.runLoadingThen(async () => {
          const [cat, formula] = await Promise.all([
            this.fetchCatalogue(),
            this.fetchFormula(stackId),
          ]);
          this.applyCatalogue(cat);
          // Awaited: applyFormulaPrefill loads the blend's base (21 Sep
          // 2026) and showPicker renders from this.base — a fire-and-forget
          // call would paint the cart before the base line existed.
          await this.applyFormulaPrefill(formula);
          this.showPicker();
        });
      } else if (savedState) {
        // Resume from sessionStorage. Same reason as pre-fill — show
        // the loading screen so the picker doesn't flash empty.
        await this.runLoadingThen(async () => {
          const cat = await this.fetchCatalogue();
          this.applyCatalogue(cat);
          this.applySavedState(savedState);
          // B5: a fresh ?base= deep link wins over whatever the saved build
          // used; otherwise the saved base restores silently.
          await this.loadBase(params.get('base') || savedState.base_slug);
          this.showPicker();
          this.renderAll();
          // Founder 2026-08-14: no "picked up where you left off" toast —
          // the restore happens silently.
        });
      } else {
        // Cold visit. ONE SCREEN, NO STEPS (founder, 28 Sep 2026): every
        // arrival lands straight on the builder with the whole catalogue
        // and the manufacturing fee already in the blend. No base picker
        // before it, no naming screen after it. The picker shell shows
        // immediately with skeleton cards while the catalogue (and any
        // ?base= deep link from a base product page) loads.
        this.showPicker();
        try {
          const cat = await this.fetchCatalogue();
          this.applyCatalogue(cat);
          await this.loadBase(params.get('base'));
          // Re-render the grid + cart now that the catalogue is in.
          this.renderAll();
          // Event name and step number kept as-is so the B5 funnel baseline
          // (builder viewed) stays comparable; there are no other steps now.
          if (window.ytTrack) { try { ytTrack('funnel_step_viewed', { step: 2, base: this.baseSlug || 'scratch' }); } catch (_e) {} }
        } catch (e) {
          // Surface the error via the loading section's error state.
          // We still need a place to show "something went wrong" so the
          // customer isn't stuck staring at skeletons forever.
          this.showError(
            e && e.message ? e.message : this.copy.networkError,
            () => this.init(),
          );
        }
      }

      // The naming step (and its ?step=name / ?rename=1 URLs) is gone —
      // an old bookmark or history entry just has the params tidied away.
      if (params.has('step') || params.has('rename')) {
        try {
          const clean = new URL(window.location.href);
          clean.searchParams.delete('step');
          clean.searchParams.delete('rename');
          window.history.replaceState({}, '', clean.pathname + (clean.search || '') + clean.hash);
        } catch (_e) { /* cosmetic only */ }
      }
    }

    // ─── state transitions ──────────────────────────────────────────

    showLoading() {
      if (!this.loadingSection || !this.pickerSection) return;
      this.loadingSection.hidden = false;
      this.loadingSection.dataset.state = 'loading';
      this.pickerSection.hidden = true;
      const errEl = this.loadingSection.querySelector('[data-state-error]');
      const loadEl = this.loadingSection.querySelector('[data-state-loading]');
      const subEl = this.loadingSection.querySelector('[data-state-submitting]');
      if (errEl) errEl.hidden = true;
      if (loadEl) loadEl.hidden = false;
      if (subEl) subEl.hidden = true;
    }

    /**
     * Submit-specific loader: "Making your formula" + determinate progress bar.
     *
     * Used ONLY for the ADD TO BASKET / VIEW FULL FORMULA submit chain.
     * Different from showLoading() so the customer reads a clear commitment
     * during the ~7s wait and doesn't bounce. The bar is driven by real
     * stage completions (submit → 60%, cart-add → 95%) plus a slow drift
     * between stages so it doesn't look frozen. Final jump to 100% happens
     * on success right before the redirect.
     */
    showSubmitLoader() {
      if (!this.loadingSection || !this.pickerSection) return;
      this.loadingSection.hidden = false;
      this.loadingSection.dataset.state = 'submitting';
      this.pickerSection.hidden = true;
      const errEl = this.loadingSection.querySelector('[data-state-error]');
      const loadEl = this.loadingSection.querySelector('[data-state-loading]');
      const subEl = this.loadingSection.querySelector('[data-state-submitting]');
      if (errEl) errEl.hidden = true;
      if (loadEl) loadEl.hidden = true;
      if (subEl) subEl.hidden = false;

      // 2026-08-13: the shared 3-step loader (yt-submit-steps.js) owns the
      // title + chips + bar. The heading element doubles as the component's
      // step title, so it now reads "Formulating your blend…" etc. instead
      // of a static heading; the subline keeps its stay-here copy.
      const sublineEl = this.loadingSection.querySelector('[data-yt-byo-submit-subline]');
      // Two fixed lines (founder 2026-08-13) — pre-line CSS honours the \n.
      if (sublineEl) sublineEl.textContent = 'Hang tight —\nplease don’t close this screen.';
      const stepsHost = this.loadingSection.querySelector('[data-yt-submit-steps]');
      if (stepsHost && window.YtSubmitSteps) {
        // Fresh instance per submit — rebuilds the chips/bar DOM clean.
        // FIXED choreography (founder 2026-08-13: same designed pacing
        // for everyone; the backend runs in parallel): 6s / 6s /
        // 6s-to-90%, then the hold is the reality gate — finishSubmitFill
        // releases it once the variant is verified buyable (or in the
        // basket). Retune by editing this array only.
        this.submitSteps = new YtSubmitSteps(stepsHost, {
          titleEl: this.loadingSection.querySelector('[data-yt-byo-submit-heading]'),
          ariaLabel: 'Creating your formula',
        });
        this.submitSteps.startTimeline({ durations: [5400, 6600, 6000], holdAt: 75 });
      } else {
        this.submitSteps = null;
        this.setSubmitStep('Building your blend…');
      }

      // Take the customer to the TOP so they actually SEE this loader. On
      // mobile the CTAs live in a bottom-sheet, so they're scrolled down when
      // they tap — without this the loader renders above the fold and the
      // submit looks like it silently failed (it didn't; it just redirected
      // ~7s later off-screen). Same behaviour as desktop, where the cart is a
      // side rail near the top.
      try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }
      catch (_e) { window.scrollTo(0, 0); }
    }

    // startSubmitDrift/setSubmitProgress removed 2026-08-13 — the shared
    // 3-step component (yt-submit-steps.js) owns every bar glide now.
    stopSubmitDrift() {
      // Steps component owns the bar now. "Stop" = freeze in place (error
      // paths) — after finish() the bar is at 100 so this is harmless on
      // success paths that still call it. Legacy-timer guard kept.
      if (this.submitSteps) this.submitSteps.fail();
      if (this._submitDriftTimer) {
        clearInterval(this._submitDriftTimer);
        this._submitDriftTimer = null;
      }
    }

    /** Update the loader's title line (the component's step title el). */
    setSubmitStep(stepText) {
      if (typeof stepText !== 'string') return;
      const title = this.loadingSection.querySelector('[data-yt-byo-submit-heading]');
      if (title) title.textContent = stepText;
      const step = this.loadingSection.querySelector('[data-yt-byo-submit-step]');
      if (step) step.textContent = stepText;
    }

    /** Complete the steps bar (100%) + set the final title. */
    finishSubmitFill(stepText) {
      if (this.submitSteps) {
        this.submitSteps.finish(stepText);
        return;
      }
      this.setSubmitStep(stepText);
    }

    showPicker() {
      this.stopSubmitDrift();
      if (!this.loadingSection || !this.pickerSection) return;
      this.loadingSection.hidden = true;
      this.pickerSection.hidden = false;
      this.renderAll();
    }

    showError(message, retry) {
      if (!this.loadingSection || !this.pickerSection) return;
      this.loadingSection.hidden = false;
      this.pickerSection.hidden = true;
      const errEl = this.loadingSection.querySelector('[data-state-error]');
      const loadEl = this.loadingSection.querySelector('[data-state-loading]');
      if (loadEl) loadEl.hidden = true;
      if (errEl) errEl.hidden = false;
      const detail = this.loadingSection.querySelector('[data-yt-byo-error-detail]');
      if (detail) detail.textContent = message;
      const btn = this.loadingSection.querySelector('[data-yt-byo-retry]');
      if (btn) btn.onclick = retry;
    }

    async runLoadingThen(fn) {
      this.showLoading();
      try { await fn(); }
      catch (e) {
        this.showError(e && e.message ? e.message : this.copy.networkError, () => this.runLoadingThen(fn));
      }
    }

    // ─── transport ──────────────────────────────────────────────────

    async fetchCatalogue() {
      // 2026-09-02: the section starts this fetch inline at parse time so
      // the 388KB catalogue is already in flight before this bundle runs.
      if (window.__ytCataloguePrefetch) {
        const pre = window.__ytCataloguePrefetch;
        window.__ytCataloguePrefetch = null;
        try {
          const preRes = await pre;
          if (preRes && preRes.ok) return await preRes.json();
        } catch (_e) { /* fall through to the timed fetch */ }
      }
      const res = await fetchWithTimeout(
        this.proxyBase + '/catalogue',
        { credentials: 'same-origin' },
        20000,
        this.copy.networkError,
      );
      if (!res.ok) throw new Error(this.copy.networkError);
      return await res.json();
    }

    async fetchFormula(id) {
      const res = await fetchWithTimeout(
        this.proxyBase + '/formula/' + encodeURIComponent(id),
        { credentials: 'same-origin' },
        20000,
        'Could not load that blend.',
      );
      if (!res.ok) throw new Error('Could not load that blend.');
      return await res.json();
    }

    async submitFormula(branch, onProgress) {
      const tick = (pct, label) => { if (typeof onProgress === 'function') onProgress(pct, label); };

      // Hashing the stack is cheap but lets us pop the bar off 0%
      // instantly so the customer sees motion the moment they click.
      const editedAfterAi = this.preFillHash
        ? (await hashStack(this.stack)) !== this.preFillHash
        : false;
      tick(15, 'Building your blend…');

      const body = {
        stack: {
          // No customer naming (founder, 28 Sep 2026): "My Blend", or the
          // base's tin name. The server enforces the same rule regardless.
          stack_name: this.defaultTinName(),
          // Empty array if customer didn't declare goals — iter-20 fix.
          // Previously fell back to ['general'], but the server validates
          // every tag against the catalogue's known goal_tags set, and
          // 'general' isn't there, so every direct-BYO submit failed
          // 422 'primary_goals[0]: general is not a known goal tag'.
          primary_goals: this.primaryGoals,
          ingredients: this.stack.map(i => ({ ingredient_id: i.ingredient_id, amount_mg: i.amount_mg })),
        },
        formula_id_prefill: this.formulaIdPrefill || undefined,
        edited_after_ai: editedAfterAi,
        // B5: the locked base under this blend (ingredients above are the
        // ADD-ONS only — the server stores the combined snapshot).
        base_slug: this.baseSlug || undefined,
        // NOTE: customer identity is NOT sent from here. window.Shopify.customer
        // is never defined on this page, so the old customer_id/customer_email
        // reads always resolved to null — dead code. Identity is resolved
        // server-side from the App Proxy session header (submit.ts uses the
        // session's customer_id/email, treating these body fields as optional).
      };

      // ALWAYS-FRESH (2026-06-26): every press mints a brand-new variant via
      // /formula/submit — no in-place update / resubmit, no reuse, regardless
      // of what changed or whose stack it is. A fresh Idempotency-Key per press
      // means the server never collapses two distinct presses into one variant
      // (it only de-dupes a retried POST of the SAME key — a network double-fire).
      const idemKey = uuidv4();
      const sharedHeaders = {
        'Content-Type': 'application/json',
        'Idempotency-Key': idemKey,
      };

      // Bar tick BEFORE the fetch starts so the customer sees motion the moment
      // they click — the bar would otherwise sit at 15% for the round-trip.
      tick(30, 'Sending to our formulator…');

      const res = await fetchWithTimeout(this.proxyBase + '/formula/submit', {
        method: 'POST',
        credentials: 'same-origin',
        headers: sharedHeaders,
        body: JSON.stringify(body),
      }, 45000, "This is taking longer than expected — your formula wasn't saved. Please try again.");
      // Network response in. Most of the wall-clock lives here
      // (formulator → variant-manager → publishablePublish).
      tick(75, 'Saving your formula…');

      if (!res.ok) {
        let text = '';
        let err = null;
        try {
          text = await res.text();
          err = JSON.parse(text);
        } catch (_e) {}
        // 429 is our own limiter (JSON envelope); 430 / "local_rate_limited"
        // is Shopify's storefront throttle answering for the App Proxy —
        // no envelope, so without this it read as a generic network error.
        if (res.status === 429 || res.status === 430 || /local_rate_limited/i.test(text || '')) {
          const retryAfter = parseInt(res.headers.get('retry-after') || '60', 10);
          const minutes = Math.max(1, Math.round(retryAfter / 60));
          throw new Error(this.copy.rateLimitPrefix + ' ' + minutes + ' minute' + (minutes === 1 ? '' : 's') + '.');
        }
        let detail = this.copy.networkError;
        // Prefer the validator's human-readable issue list (e.g. which
        // ingredient/dose was rejected) over the generic envelope message.
        if (err) {
          const human = err.error && err.error.details && err.error.details.message_for_human;
          if (human) detail = human;
          else if (err.error && err.error.message) detail = err.error.message;
        }
        throw new Error(detail);
      }
      return await res.json();
    }

    // ─── customer portal: "Change my stack" ─────────────────────────

    /**
     * Relabel + repurpose the CTAs for portal (swap) mode. The picker buttons
     * are plain-text (yt-byo-picker.liquid) so textContent is safe.
     */
    applyPortalModeCtas() {
      // Single-CTA model (2026-07-09): the VIEW FORMULA buttons are the only
      // submit CTAs left, and handleSubmit already routes every portal-mode
      // submit down the subscription-swap branch — so just relabel them.
      // Query fresh (not this.viewFormulaBtns) so this works no matter when
      // in the init sequence it's called.
      const label = 'UPDATE MY SUBSCRIPTION';
      this.pickerSection
        .querySelectorAll('[data-yt-byo-view-formula]')
        .forEach(b => { b.textContent = label; b.dataset.ytOrigLabel = label; });
      if (this.mobileCtaBtn) {
        this.mobileCtaBtn.textContent = label;
        this.mobileCtaBtn.dataset.ytOrigLabel = label;
      }
    }

    /**
     * Re-point the customer's existing subscription at the freshly-built
     * variant via the formulator swap route. Returns the before/after price
     * payload (for the disclosure) or throws a friendly error.
     */
    /**
     * BUNDLE MODE (B3): add the whole blend to the cart as Recharge dynamic-
     * bundle component lines. `bundle` is the submit response payload - every
     * child line carries the same _rc_bundle properties (+ Tin name) and the
     * 30-day selling plan (subscription-by-default, mirroring the legacy
     * basket branch / decision 0005; in the bundle world the plan carries the
     * 20% subscriber discount, so cart price = the quoted monthly price).
     * Components are long-lived catalogue variants - no index-lag retry
     * needed (the legacy 60s 422 loop existed only for freshly-minted
     * variants).
     */
    /** One spoon per CART: skip the auto-add when a spoon line already
     *  exists (repeat adds/edits were stacking spoons — founder,
     *  verification run 2026-08-26). */
    async spoonLineNeeded() {
      // Free in BOTH modes (the spoon product is priced £0.00 — founder
      // 2026-08-26); still exactly ONE per cart.
      if (!this.spoonVariantId) return false;
      try {
        const cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
        return !(cart.items || []).some(i => Number(i.variant_id) === Number(this.spoonVariantId));
      } catch (_e) {
        return true;
      }
    }

    async addBundleToShopifyCart(bundle) {
      const items = (bundle.items || []).map((i) => {
        const line = {
          id: Number(i.variant_id),
          quantity: i.quantity || 1,
          properties: bundle.properties || {},
        };
        // B4 finding: each product carries ITS OWN 30-day plan id
        // (Recharge inheritance mints per-product plans; a shared id is
        // invalid on other products' lines and checkout bounces).
        // One-time mode: no plans — the same component lines check out as
        // a plain order (proved by order #1116).
        if (i.selling_plan_30d && this.purchaseMode !== 'onetime') line.selling_plan = Number(i.selling_plan_30d);
        return line;
      });
      // Spoon now rides INSIDE the bundle payload (formulator includes it
      // in items + registration) — no separate push.
      const res = await fetchWithTimeout(
        '/cart/add.js',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: items }),
        },
        20000,
        this.copy.networkError,
      );
      if (!res.ok) {
        let detail = this.copy.networkError;
        try {
          const err = await res.json();
          if (err && (err.description || err.message)) detail = err.description || err.message;
        } catch (_e) {}
        throw new Error(detail);
      }
      return await res.json();
    }

    /**
     * BUNDLE MODE (B3): the mint-and-swap replacement - the formulator
     * replaces the subscription's bundle_selection contents in place
     * (instant; the next charge extracts the new blend).
     */
    async updateBundleSubscription(subscriptionId, formulaId) {
      const res = await fetchWithTimeout(
        this.proxyBase + '/account/subscriptions/' + encodeURIComponent(subscriptionId) + '/update-bundle',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ formula_id: formulaId }),
        },
        30000,
        "This is taking longer than expected - your subscription wasn't updated. Please try again.",
      );
      if (!res.ok) {
        let detail = this.copy.networkError;
        try {
          const err = await res.json();
          if (err && err.message) detail = err.message;
        } catch (_e) {}
        throw new Error(detail);
      }
      return await res.json();
    }

    async swapSubscription(subscriptionId, variantId, formulaId) {
      const res = await fetchWithTimeout(
        this.proxyBase + '/account/subscriptions/' + encodeURIComponent(subscriptionId) + '/swap',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ shopify_variant_id: variantId, formula_id: formulaId || undefined }),
        },
        30000,
        "This is taking longer than expected — your subscription wasn't updated. Please try again.",
      );
      if (!res.ok) {
        let detail = this.copy.networkError;
        try { const err = await res.json(); if (err && err.message) detail = err.message; } catch (_e) {}
        throw new Error(detail);
      }
      return await res.json();
    }

    // ─── catalogue + state hydration ────────────────────────────────

    applyCatalogue(payload) {
      // Adopt the live pricing model (floor + plan multipliers) from the payload
      // so admin Pricing-tab changes show in the live preview. Guard each field.
      const p = payload && payload.pricing;
      if (p) {
        if (Number.isFinite(Number(p.variant_floor))) VARIANT_FLOOR = Number(p.variant_floor);
        if (Number.isFinite(Number(p.sub_multiplier))) SUB_DISCOUNT = Number(p.sub_multiplier);
        if (Number.isFinite(Number(p.bundle_multiplier))) BUNDLE_DISCOUNT = Number(p.bundle_multiplier);
        // >= 0 (not > 0): the bundle world serves prepaid_floor 0 (D21) and
        // a rejected zero would leave the dead £45 gate live client-side.
        if (Number.isFinite(Number(p.prepaid_floor)) && Number(p.prepaid_floor) >= 0) PREPAID_FLOOR = Number(p.prepaid_floor);
        BUNDLES_MODE = !!p.bundles_enabled;
        if (Number.isFinite(Number(p.base))) PRICE_BASE = Number(p.base);
        if (Number.isFinite(Number(p.one_time_multiplier)) && Number(p.one_time_multiplier) > 0) ONE_TIME_MULTIPLIER = Number(p.one_time_multiplier);
        if (Number.isFinite(Number(p.taper_after))) PRICE_TAPER_AFTER = Number(p.taper_after);
        if (Number.isFinite(Number(p.taper_gbp))) PRICE_TAPER_GBP = Number(p.taper_gbp);
      }
      // Need the pricing fields to price; `raw_cost_per_mg` is the gate.
      this.catalogue = (payload.ingredients || []).filter(i => i.raw_cost_per_mg != null);
      this.catalogueById = new Map(this.catalogue.map(i => [i.id, i]));
    }

    /**
     * Pre-fill the stack from a `?prefill=<slug>:<dose>,...` query
     * string (used by the yt-stacks "Start with this formula" CTAs
     * on the home page). Each pair is matched against catalogue
     * `slug` (case-insensitive). Doses are parsed loosely:
     *
     *   - "200mg" / "200 mg" / "200" → 200 mg
     *   - "25mcg" / "25 mcg"          → 0.025 mg  (catalogue is mg)
     *   - "3g"  / "3 g"               → 3000 mg
     *   - anything else               → fall back to default_dose_mg
     *
     * Slugs that don't match any catalogue ingredient are silently
     * skipped so a partly-stale CTA still pre-selects what it can.
     */
    applyPrefillBySlugs(rawPairs) {
      if (!rawPairs || !this.catalogue) return;
      const bySlug = new Map(
        this.catalogue.map(i => [String(i.slug || '').toLowerCase(), i]),
      );
      const stack = [];
      const seen = new Set();
      for (const pair of String(rawPairs).split(',')) {
        const [rawSlug, rawDose] = pair.split(':');
        const slug = (rawSlug || '').trim().toLowerCase();
        if (!slug || seen.has(slug)) continue;
        const ing = bySlug.get(slug);
        if (!ing) continue;
        seen.add(slug);
        const dose = parseDoseToMg(rawDose, ing.default_dose_mg);
        stack.push({ ingredient_id: ing.id, amount_mg: dose });
      }
      this.stack = stack;
      // No formula id — these are example stacks, not saved formulas.
      // Submit takes the "create new variant" path (always-fresh).
      this.formulaIdPrefill = null;
    }

    /**
     * Seed the builder from a stored formula (?stack=<id>).
     *
     * BASED BLENDS (Lucas, 21 Sep 2026 — "fix this everywhere"). The stored
     * snapshot is the COMBINED blend: the base recipe's own rows PLUS the
     * add-ons, because the leaflet and customer service show the whole
     * blend. Seeding the picker from that flat list and never reading
     * `base_slug` left this.baseSlug null, so the next FORMULATE re-posted
     * the base's own ingredients as components with no base: a 422 on the
     * base's below-menu doses (Daily Energy carries vitamin C at 80mg where
     * the component menu starts at 100mg), or a BASELESS duplicate priced
     * with the £15 dispensing line — and in portal mode the follow-up
     * /update-bundle then saw no base and cancelled the customer's base
     * component subscription.
     *
     * So: load the base (exactly as the ?base= and saved-state branches of
     * init do) and seed the stack from the ADD-ONS only. The split mirrors
     * the server's own rule (formulator src/formula/pdp-payload.ts,
     * isBaseSnapshotRow): an add-on row carries a numeric price share, a
     * base row never does — which keeps a TOP-UP of a base ingredient in
     * the builder, since it carries a share of its own. submitFormula
     * already sends this.baseSlug and persist() already round-trips it, so
     * nothing downstream changes.
     */
    async applyFormulaPrefill(formula) {
      this.formulaIdPrefill = formula.id;
      // ALWAYS-FRESH (2026-06-26): a ?stack=<id> prefill is a read-only
      // TEMPLATE — we seed the picker from it but never reuse or mutate its
      // variant. So we deliberately do NOT stash the existing variant id /
      // stack hash / eligibility flag any more; the next submit always creates
      // a brand-new variant. This is what stops a shared link from letting one
      // visitor rewrite another customer's variant (and their subscription).
      this.primaryGoals = formula.primary_goals || [];
      const baseSlug =
        typeof formula.base_slug === 'string' && formula.base_slug ? formula.base_slug : null;
      // Same soft-failing loader the other entry points use: a held or
      // renamed base leaves this.base null and the blend opens as a plain
      // component build rather than dead-ending on a shared link.
      if (baseSlug) await this.loadBase(baseSlug);
      const rows = (formula.ingredients || [])
        .filter(i => this.catalogueById.has(i.ingredient_id));
      // Keyed on this.baseSlug, not the payload's: if the base could not be
      // loaded we keep the whole combined blend rather than silently drop
      // the base's ingredients from the customer's build.
      // Lucas, 21 Sep 2026: the server now splits this for us (add_ons on GET /formula/:id). Prefer it: the share rule below cannot read a snapshot written by bundle intake, where the base and its add-ons are summed with no markers, and silently yields NOTHING for the 19 live blends in that shape. Fall back to the share rule only for a cached older payload.
      const servedAddOns = Array.isArray(formula.add_ons) ? formula.add_ons : null;
      this.stack = this.baseSlug && servedAddOns
        ? servedAddOns
            .filter(a => this.catalogueById.has(a.ingredient_id))
            .map(a => ({ ingredient_id: a.ingredient_id, amount_mg: Number(a.amount_mg) }))
        : (this.baseSlug
            ? rows.filter(i => typeof i.price_share_gbp === 'number')
            : rows
          ).map(i => ({ ingredient_id: i.ingredient_id, amount_mg: this.gridAmount(i.ingredient_id, i.amount_mg) }));
      hashStack(this.stack).then(h => { this.preFillHash = h; });
    }

    applySavedState(saved) {
      this.primaryGoals = saved.primary_goals || [];
      this.formulaIdPrefill = saved.formula_id_prefill || null;
      this.preFillHash = saved.pre_fill_hash || null;
      this.stack = (saved.ingredients || [])
        .filter(i => this.catalogueById.has(i.ingredient_id))
        .map(i => ({ ingredient_id: i.ingredient_id, amount_mg: this.gridAmount(i.ingredient_id, i.amount_mg) }));
    }

    /** snapToGrid against the catalogue row; an unknown ingredient keeps its number (it is filtered out elsewhere). */
    gridAmount(ingredientId, mg) {
      const ing = this.catalogueById.get(ingredientId);
      return ing ? snapToGrid(ing, mg) : Number(mg);
    }

    persist() {
      store.write({
        ingredients: this.stack,
        primary_goals: this.primaryGoals,
        pre_fill_hash: this.preFillHash,
        formula_id_prefill: this.formulaIdPrefill,
        base_slug: this.baseSlug,
      });
    }

    /**
     * B5: load the base for ?base=<slug> (or a restored slug) from the
     * /bases endpoint. Failing softly leaves a scratch build — a base
     * product page's deep link is the only entry that sets the param, so a
     * miss is a held/renamed base, never a customer-made state.
     */
    async loadBase(slug) {
      if (!slug) return;
      try {
        const res = await fetchWithTimeout(
          this.proxyBase + '/bases',
          { credentials: 'same-origin' },
          20000,
          'Could not load the bases.',
        );
        if (!res.ok) return;
        const data = await res.json();
        const base = (data.bases || []).find(b => b.slug === slug) || null;
        if (!base) return;
        this.base = base;
        this.baseSlug = base.slug;
        this.baseIngredientIds = new Set(
          (base.ingredients || [])
            .map(ing => {
              const cat = this.catalogue.find(c => c.slug === ing.slug);
              return cat ? cat.id : null;
            })
            .filter(Boolean),
        );
      } catch (_e) { /* scratch build */ }
    }

    /**
     * Drop the base (Lucas, 24 Sep 2026): the blend becomes a scratch build
     * of whatever was added on top, priced on the no-base model from the
     * next preview. The add-ons stay — the customer still wants them. The
     * base's own ingredients never sat in `this.stack`, so there is nothing
     * to take apart; the demotion and duplicate warning simply switch off.
     */
    removeBase() {
      if (!this.base) return;
      const name = this.base.name;
      this.base = null;
      this.baseSlug = null;
      this.baseIngredientIds = new Set();
      this.dupAcknowledged = new Set();
      this.persist();
      // A reload must not bring the base back: ?base=<slug> wins over the
      // saved state on load, so take it off the URL.
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('base')) {
          url.searchParams.delete('base');
          window.history.replaceState(window.history.state, '', url.pathname + (url.search || '') + url.hash);
        }
      } catch (_e) { /* old browser: the saved state still has no base */ }
      this.renderGrid();
      this.renderCart();
      this.showToast(name + ' base removed. Your added ingredients are still here.');
    }

    showToast(msg, ms) {
      // Minimal — no library; just inject + auto-dismiss.
      const t = document.createElement('div');
      t.textContent = msg;
      t.style.cssText = 'position:fixed;bottom:5rem;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.85);color:#fff;padding:0.625rem 1rem;border-radius:9999px;font-family:Quicksand,sans-serif;font-size:0.875rem;z-index:1500;';
      document.body.appendChild(t);
      t.style.maxWidth = 'min(92vw, 520px)';
      t.style.textAlign = 'center';
      if (ms) t.style.borderRadius = '14px'; // long messages wrap
      setTimeout(() => t.remove(), ms || 3000);
    }

    // ─── picker bindings ────────────────────────────────────────────

    bindPicker() {
      this.searchInput = this.pickerSection.querySelector('[data-yt-byo-search]');
      this.gridEl = this.pickerSection.querySelector('[data-yt-byo-grid]');
      this.countEl = this.pickerSection.querySelector('[data-yt-byo-count]');
      this.emptyEl = this.pickerSection.querySelector('[data-yt-byo-empty]');
      this.filtersBody = this.pickerSection.querySelector('[data-yt-byo-filters-body]');
      this.filtersAside = this.pickerSection.querySelector('[data-yt-byo-filters]');
      // Mobile-only A–Z jump rail (alphabetical sort only). Touch-scrub: drag a
      // finger down the rail and we jump to whichever letter is under it.
      this.azEl = this.pickerSection.querySelector('[data-yt-byo-az]');
      if (this.azEl) {
        // Touch-scrub the whole rail like a scrollbar: jump to whichever letter
        // is nearest the finger's Y (not just a direct hit on a letter), so
        // dragging over the track/thumb works too.
        const onAzTouch = (e) => {
          const t = e.touches && e.touches[0];
          if (!t) return;
          const btn = this.letterButtonNearestY(t.clientY);
          if (btn) {
            const letter = btn.getAttribute('data-letter');
            if (letter && letter !== this._lastAzLetter) {
              this._lastAzLetter = letter;
              this.jumpToLetter(letter);
            }
          }
        };
        this.azEl.addEventListener('touchstart', onAzTouch, { passive: true });
        this.azEl.addEventListener('touchmove', onAzTouch, { passive: true });
        this.azEl.addEventListener('touchend', () => { this._lastAzLetter = null; }, { passive: true });

        // Keep the scroll thumb in sync with the page scroll (rAF-throttled).
        const onAzScroll = () => {
          if (this._azScrollRaf) return;
          this._azScrollRaf = requestAnimationFrame(() => {
            this._azScrollRaf = null;
            this.updateAzThumb();
          });
        };
        window.addEventListener('scroll', onAzScroll, { passive: true });
        window.addEventListener('resize', onAzScroll, { passive: true });
        // Page height can change after load (lazy images below the grid).
        window.addEventListener('load', onAzScroll, { passive: true });
      }

      this.cartListEl = this.pickerSection.querySelector('[data-yt-byo-cart-list]');
      this.cartEmptyEl = this.pickerSection.querySelector('[data-yt-byo-cart-empty]');
      this.priceEl = this.pickerSection.querySelector('[data-yt-byo-price]');
      // Iter-15: single-line summary on mobile sticky bar.
      this.summaryLineEl = this.pickerSection.querySelector('[data-yt-byo-cart-summary-line]');
      this.summaryCountEl = this.pickerSection.querySelector('[data-yt-byo-cart-summary-count]');
      this.livePriceEl = this.pickerSection.querySelector('[data-yt-byo-live-price]');
      // £45-minimum progress bars (founder 2026-08-14) — one in the closed
      // mobile bar, one under the cart-panel price line. Persistent elements
      // (never innerHTML-rebuilt) so the width transition can animate.
      this.minProgressEls = Array.from(this.pickerSection.querySelectorAll('[data-yt-byo-min-progress]'));
      this.minProgressFills = Array.from(this.pickerSection.querySelectorAll('[data-yt-byo-min-progress-fill]'));
      this.cartNavUp = this.pickerSection.querySelector('[data-yt-byo-cart-nav-up]');
      this.cartNavDown = this.pickerSection.querySelector('[data-yt-byo-cart-nav-down]');
      this.stackNameEl = this.pickerSection.querySelector('[data-yt-byo-stack-name]');
      // No-price model (founder 2026-07-09): ADD TO BASKET is gone; VIEW
      // FORMULA is the single submit CTA and exists twice — in the expanded
      // panel and on the collapsed mobile bar — so it's an array.
      this.viewFormulaBtns = Array.from(
        this.pickerSection.querySelectorAll('[data-yt-byo-view-formula]')
      );
      // Deprecated in iter-7 — the dual ADD TO BASKET / VIEW FULL FORMULA
      // CTA now works on both mobile and desktop, so the standalone
      // mobile primary CTA was removed from the Liquid. May or may not
      // be in the DOM depending on theme version, so we tolerate null.
      this.mobileCtaBtn = this.pickerSection.querySelector('[data-yt-byo-mobile-cta]');
      this.errorRegion = this.pickerSection.querySelector('[data-yt-byo-error]');

      this.cartAside = this.pickerSection.querySelector('[data-yt-byo-cart]');

      // Filters open/close
      this.pickerSection.querySelectorAll('[data-yt-byo-filters-open]').forEach(b => {
        b.addEventListener('click', () => this.filtersAside.dataset.open = 'true');
      });
      this.pickerSection.querySelectorAll('[data-yt-byo-filters-close]').forEach(b => {
        b.addEventListener('click', () => this.filtersAside.dataset.open = 'false');
      });
      this.pickerSection.querySelectorAll('[data-yt-byo-filters-reset]').forEach(b => {
        b.addEventListener('click', () => this.resetFilters());
      });

      // Cart open/close on mobile
      this.pickerSection.querySelectorAll('[data-yt-byo-cart-toggle]').forEach(b => {
        b.addEventListener('click', () => {
          const cur = this.cartAside.dataset.expanded === 'true';
          this.cartAside.dataset.expanded = cur ? 'false' : 'true';
        });
      });
      this.pickerSection.querySelectorAll('[data-yt-byo-cart-close]').forEach(b => {
        b.addEventListener('click', () => this.cartAside.dataset.expanded = 'false');
      });

      // Add more — scroll catalogue into view + focus search
      this.pickerSection.querySelectorAll('[data-yt-byo-add-more]').forEach(b => {
        b.addEventListener('click', () => {
          this.cartAside.dataset.expanded = 'false';
          this.searchInput.scrollIntoView({ behavior: 'smooth', block: 'start' });
          this.searchInput.focus();
        });
      });

      // Search
      this.searchInput.addEventListener('input', debounce(() => {
        this.filters.search = this.searchInput.value.trim().toLowerCase();
        this.renderGrid();
        this.revealCatalogueTop();
      }, 100));
      // Analytics: a second, much slower debounce so we only log a SETTLED
      // search term (never per-keystroke) and skip 1-char noise.
      this.searchInput.addEventListener('input', debounce(() => {
        const term = this.searchInput.value.trim().toLowerCase();
        if (term.length >= 2 && window.ytTrack) ytTrack('byo_search_used', { term: term });
      }, 800));

      // CTAs. Guard the binds: if a theme build is missing one of these
      // buttons, an unguarded .addEventListener throws inside the constructor
      // and takes the ENTIRE controller down with it (search, grid, cart all
      // dead), which is a far worse failure than one absent CTA. renderCart()
      // already null-checks both before touching them.
      this.viewFormulaBtns.forEach(b => {
        b.addEventListener('click', () => this.handleSubmit('formula'));
      });
      // The deprecated standalone mobile CTA may still exist in older
      // theme builds — guard the bind so a missing element doesn't fail.
      if (this.mobileCtaBtn) {
        this.mobileCtaBtn.addEventListener('click', () => this.handleSubmit('formula'));
      }

      // Cart-list nav arrows — jump one ingredient row per click.
      // Reads the height of the first row to know how far to scroll.
      // Both arrows hide automatically when there's nowhere to scroll
      // in their direction (handled in updateCartNavArrows()).
      if (this.cartNavUp) {
        this.cartNavUp.addEventListener('click', () => this.scrollCartByRow(-1));
      }
      if (this.cartNavDown) {
        this.cartNavDown.addEventListener('click', () => this.scrollCartByRow(1));
      }
      // Also update visibility when the customer scrolls the list manually.
      if (this.cartListEl) {
        this.cartListEl.addEventListener('scroll', () => this.updateCartNavArrows());
      }

      // Over-max modal
      this.overMaxModal = this.pickerSection.querySelector('[data-yt-byo-overmax-modal]');
      this.pickerSection.querySelectorAll('[data-yt-byo-modal-backdrop]').forEach(b => {
        b.addEventListener('click', () => this.closeOverMaxModal());
      });

      // Ingredient INFO modal — fired by clicking the "i" badge on any
      // grid card or cart row. Same modal element for every ingredient;
      // we fill the placeholders on open, then show. Close on ×,
      // backdrop click, or Escape.
      this.infoModal = this.pickerSection.querySelector('[data-yt-byo-info-modal]');
      if (this.infoModal) {
        this.infoModalEls = {
          name:     this.infoModal.querySelector('[data-yt-byo-info-name]'),
          summary:  this.infoModal.querySelector('[data-yt-byo-info-summary]'),
          nrv:      this.infoModal.querySelector('[data-yt-byo-info-nrv]'),
          nrvValue: this.infoModal.querySelector('[data-yt-byo-info-nrv-value]'),
          add:      this.infoModal.querySelector('[data-yt-byo-info-add]'),
          image:    this.infoModal.querySelector('[data-yt-byo-info-image]'),
          pdpLink:  this.infoModal.querySelector('[data-yt-byo-info-pdp-link]'),
        };
        const close = () => this.closeInfoModal();
        // Add / remove the current ingredient straight from the popup, then
        // close so the customer drops back to the grid (founder 2026-07-12).
        if (this.infoModalEls.add) {
          this.infoModalEls.add.addEventListener('click', () => {
            if (this.infoModalIng) this.toggleIngredient(this.infoModalIng);
            close();
          });
        }
        // Guard the child derefs: a partial theme build could ship the modal
        // shell without one of these controls. An unguarded .addEventListener on
        // a missing element throws inside the constructor and kills the WHOLE
        // controller (no CTAs, no search — dead page), so tolerate nulls.
        const infoCloseBtn = this.infoModal.querySelector('[data-yt-byo-info-modal-close]');
        const infoBackdrop = this.infoModal.querySelector('[data-yt-byo-info-modal-backdrop]');
        if (infoCloseBtn) infoCloseBtn.addEventListener('click', close);
        if (infoBackdrop) infoBackdrop.addEventListener('click', close);
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !this.infoModal.hidden) close();
        });
      }
    }

    openInfoModal(ing) {
      if (!this.infoModal || !ing) return;
      if (window.ytTrack) ytTrack('byo_info_opened', { ingredient: ing.name });
      const els = this.infoModalEls;
      // Remember which ingredient the popup is showing so the Add button knows
      // what to toggle; reflect whether it is already in the blend.
      this.infoModalIng = ing;
      if (els.add) {
        const inStack = this.stack.some(s => s.ingredient_id === ing.id);
        els.add.textContent = inStack ? 'Remove from blend' : 'Add to blend';
        els.add.classList.toggle('yt-byo-info__add--added', inStack);
      }

      els.name.textContent = ing.name || 'Ingredient';

      // Single summary paragraph — prefer a real "mechanism" prose
      // block from the catalogue; fall back to tagline-plus-notes
      // when long-form copy isn't seeded yet, and to bare tagline as
      // a last resort. Capped at ~360 chars so the modal stays a
      // glance, not a read — the full prose lives on the PDP.
      const summary = this.composeSummary(ing);
      els.summary.innerHTML = summary
        .split(/\n\n+/)
        .map((p) => `<p>${this.escapeHtml(p)}</p>`)
        .join('');

      // "Default" chip — the ingredient's default dose, shown on every
      // ingredient (founder 2026-06-17, replaces NRV which only 37 had).
      els.nrvValue.textContent = `${mgToDisplay(ing.default_dose_mg, ing.unit)} ${unitLabel(ing.unit)}`;
      els.nrv.hidden = false;

      // Deep link to the per-ingredient PDP.
      els.pdpLink.href = `/products/${encodeURIComponent(ing.slug)}`;

      // Square ingredient image, bottom-right. Sourced from the Shopify product
      // (the catalogue carries no image URL); hidden until/unless one loads.
      if (els.image) {
        els.image.hidden = true;
        els.image.removeAttribute('src');
        this.loadInfoImage(ing);
      }

      this.infoModal.hidden = false;
    }

    // Fetch the ingredient's Shopify product image (slug === product handle) and
    // paint it into the open info popup. Cached per slug; fails silent (stays
    // hidden) so a missing product / image never disrupts the modal.
    loadInfoImage(ing) {
      const els = this.infoModalEls;
      if (!els || !els.image) return;
      this._infoImageCache = this._infoImageCache || {};
      const slug = ing.slug;
      const apply = (src) => {
        // Guard against a race: the customer may have opened another popup.
        if (!src || this.infoModalIng !== ing) return;
        els.image.src = src;
        els.image.alt = ing.name || '';
        els.image.hidden = false;
      };
      if (slug in this._infoImageCache) { apply(this._infoImageCache[slug]); return; }
      fetch(`/products/${encodeURIComponent(slug)}.js`, { headers: { accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => {
          let src = (p && (p.featured_image || (p.images && p.images[0]))) || null;
          if (src) {
            if (src.indexOf('//') === 0) src = 'https:' + src;
            src += (src.indexOf('?') === -1 ? '?' : '&') + 'width=256&height=256&crop=center';
          }
          this._infoImageCache[slug] = src;
          apply(src);
        })
        .catch(() => { this._infoImageCache[slug] = null; });
    }

    closeInfoModal() {
      if (this.infoModal) this.infoModal.hidden = true;
    }

    composeSummary(ing) {
      // The catalogue seeds the description as a JSON array of paragraphs
      // (story_paragraphs). Show the FULL description (no truncation) — the
      // founder wants the info popup to carry the whole paragraph, so the
      // modal grows to fit. Falls back through the older shapes + tagline.
      const sp = ing.story_paragraphs;
      let text = '';
      if (Array.isArray(sp)) {
        text = sp.filter(Boolean).join('\n\n');
      } else if (sp && typeof sp === 'object' && sp.mechanism) {
        text = sp.mechanism;
      } else if (typeof sp === 'string' && sp) {
        text = sp;
      }
      if (!text) text = ing.tagline || ing.notes_for_ai || ing.safety_notes || '';
      return text;
    }

    formatDose(v) {
      const n = Number(v);
      if (!isFinite(n)) return '—';
      return n.toString().replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
    }

    escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[c]));
    }

    // ─── filters rendering ──────────────────────────────────────────

    renderFilters() {
      this.filtersBody.innerHTML = '';
      FILTER_GROUPS.forEach(group => {
        const wrap = document.createElement('div');
        wrap.className = 'yt-byo-filter-group';
        const heading = document.createElement('h3');
        heading.className = 'yt-byo-filter-group__heading';
        heading.textContent = group.label;
        wrap.appendChild(heading);

        if (group.type === 'chips') {
          const chips = document.createElement('div');
          chips.className = 'yt-byo-filter-chips';
          group.options.forEach(opt => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'yt-byo-filter-chip';
            btn.textContent = opt.label;
            const pressed = this.filters[group.id].has(opt.value);
            btn.setAttribute('aria-pressed', String(pressed));
            btn.addEventListener('click', () => {
              if (this.filters[group.id].has(opt.value)) this.filters[group.id].delete(opt.value);
              else this.filters[group.id].add(opt.value);
              // Analytics: only when the chip was SELECTED (not deselected).
              if (this.filters[group.id].has(opt.value) && window.ytTrack) {
                ytTrack('byo_filter_selected', { filter: opt.label });
              }
              this.renderFilters();
              this.renderGrid();
              this.revealCatalogueTop();
            });
            chips.appendChild(btn);
          });
          wrap.appendChild(chips);
        } else {
          group.options.forEach(opt => {
            const lbl = document.createElement('label');
            lbl.className = 'yt-byo-filter-option';
            const input = document.createElement('input');
            input.type = group.type === 'radio' ? 'radio' : 'checkbox';
            input.name = 'yt-byo-' + group.id;
            input.value = opt.value;
            if (group.type === 'radio') input.checked = this.filters[group.id] === opt.value;
            else input.checked = this.filters[group.id].has(opt.value);
            input.addEventListener('change', () => {
              if (group.type === 'radio') {
                this.filters[group.id] = opt.value;
              } else {
                if (input.checked) this.filters[group.id].add(opt.value);
                else this.filters[group.id].delete(opt.value);
              }
              // Analytics: radios always report the new choice; checkboxes
              // only when ticked (unticking isn't a "selection").
              if ((group.type === 'radio' || input.checked) && window.ytTrack) {
                ytTrack('byo_filter_selected', { filter: opt.label });
              }
              this.renderGrid();
              this.revealCatalogueTop();
            });
            const txt = document.createElement('span');
            txt.textContent = opt.label;
            lbl.appendChild(input);
            lbl.appendChild(txt);
            wrap.appendChild(lbl);
          });
        }
        this.filtersBody.appendChild(wrap);
      });
    }

    /* Narrowing the list collapses the page under the customer: browsing
     * deep in the 200-card grid and then searching drops the document from
     * ~12,500px to ~2,800px, so the browser clamps the scroll and leaves
     * them well BELOW the (now short) list — seeing neither the matches nor
     * their Selected ingredients at the top of the grid (founder
     * 2026-07-25: "when they search it does not show the selected
     * ingredients"). Pull the search bar back into view so the results and
     * the Selected section under it are what they're looking at.
     *
     * Only ever scrolls UP: if the catalogue head is already on screen the
     * customer keeps their place. The offset clears the site header + the
     * ticker, which pin over the top of the viewport once revealed. */
    revealCatalogueTop() {
      const anchor = this.searchInput || this.gridEl;
      if (!anchor) return;
      const cs = getComputedStyle(document.documentElement);
      const px = (name) => parseFloat(cs.getPropertyValue(name)) || 0;
      const headroom = px('--header-height') + px('--yt-ticker-height') + 12;
      const top = anchor.getBoundingClientRect().top;
      if (top < headroom) window.scrollBy(0, top - headroom);
    }

    resetFilters() {
      this.filters = {
        sort: this.filters.sort,     // keep the chosen sort order
        used_for: new Set(),
        category: new Set(),
        popularity: new Set(),
        search: this.filters.search, // keep search text
      };
      this.renderFilters();
      this.renderGrid();
    }

    // ─── grid rendering ─────────────────────────────────────────────

    /**
     * Scores for the current search term, memoised per term. The index is
     * built once on first search — the catalogue is loaded by then and
     * indexing ~200 rows costs less than one frame.
     */
    currentSearchScores() {
      const q = this.filters.search;
      if (!q) return null;
      if (!this._searchIndex) this._searchIndex = buildSearchIndex(this.catalogue);
      if (this._scoresFor !== q) {
        this._scoresFor = q;
        this._scores = searchScores(this._searchIndex, q);
      }
      return this._scores;
    }

    filteredCatalogue() {
      const scores = this.currentSearchScores();
      return this.catalogue.filter(i => {
        // Used for (any-match across goal_tags)
        if (this.filters.used_for.size > 0) {
          const tags = i.goal_tags || [];
          const any = tags.some(t => this.filters.used_for.has(t));
          if (!any) return false;
        }
        // Categories
        if (this.filters.category.size > 0 && !this.filters.category.has(i.category)) return false;
        // Popularity (any-match across discovery_tags)
        if (this.filters.popularity.size > 0) {
          const tags = i.discovery_tags || [];
          const any = tags.some(t => this.filters.popularity.has(t));
          if (!any) return false;
        }
        // Search — ranked + typo-tolerant (see searchScores).
        if (scores && !scores.has(i.id)) return false;
        return true;
      });
    }

    // First letter of a name, uppercased. Non-alphabetic (numbers / symbols)
    // collapse into a single '#' bucket so they still group + jump cleanly.
    firstLetter(name) {
      const ch = (name || '').trim().charAt(0).toUpperCase();
      return /[A-Z]/.test(ch) ? ch : '#';
    }

    // Sort the filtered list per the chosen order. Alphabetical is the default
    // landing order; 'popular' restores popularity_rank ASC (NULLS LAST), the
    // server's original order.
    sortCatalogue(list) {
      const arr = list.slice();
      // Search relevance (founder 2026-07-25): searching "zi" must put Zinc
      // first, not an alphabetically-earlier ingredient that merely mentions
      // "zi" in its tagline. While a search is active the order is the search
      // engine's own score — which already encodes where and how well each
      // token matched — with shorter names then A–Z breaking ties. (Letter
      // grouping is off during search; see renderGrid.) The chosen sort radio
      // resumes when the box clears.
      const scores = this.currentSearchScores();
      if (scores) {
        arr.sort((a, b) => {
          const sa = scores.get(a.id) || 0;
          const sb = scores.get(b.id) || 0;
          if (sb !== sa) return sb - sa;
          if (a.name.length !== b.name.length) return a.name.length - b.name.length;
          return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        });
        return arr;
      }
      if (this.filters.sort === 'popular') {
        arr.sort((a, b) => {
          // B5 (founder 2026-08-27): ingredients already in the chosen BASE
          // sink to the BOTTOM of the popularity order — the customer's
          // add-on shortlist shouldn't lead with things they already have.
          const ba = this.baseIngredientIds.has(a.id) ? 1 : 0;
          const bb = this.baseIngredientIds.has(b.id) ? 1 : 0;
          if (ba !== bb) return ba - bb;
          const ra = a.popularity_rank == null ? Infinity : a.popularity_rank;
          const rb = b.popularity_rank == null ? Infinity : b.popularity_rank;
          if (ra !== rb) return ra - rb;
          return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        });
      } else {
        // Founder 2026-07-10: numeric-leading names (5-HTP…) group AFTER Z,
        // so '#' is the last divider and the jump rail ends with it.
        arr.sort((a, b) => {
          const na = this.firstLetter(a.name) === '#' ? 1 : 0;
          const nb = this.firstLetter(b.name) === '#' ? 1 : 0;
          if (na !== nb) return na - nb;
          return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        });
      }
      return arr;
    }

    renderGrid() {
      const list = this.sortCatalogue(this.filteredCatalogue());
      this.countEl.textContent = list.length;
      this.gridEl.innerHTML = '';

      // Letter grouping only makes sense in plain A–Z order — during a search
      // the list is relevance-ranked (see sortCatalogue), so grouping is off
      // and the A–Z jump rail hides with it.
      const grouped = this.filters.sort === 'alpha' && !this.filters.search;
      this.gridEl.dataset.grouped = String(grouped);

      // Selected-ingredients section — always FIRST, on every breakpoint,
      // unaffected by filters/search (founder 2026-07-23: arriving from the
      // guided builder pre-filled, nothing LOOKED selected). Cards here are
      // straight duplicates: the original stays in its alphabetical spot,
      // and because every stack mutation re-renders the whole grid, both
      // instances always agree on dose + selected state.
      this.renderSelectedSection();

      if (list.length === 0) {
        this.emptyEl.hidden = false;
        this.renderAzIndex([]);
        return;
      }
      this.emptyEl.hidden = true;

      if (grouped) {
        // Alphabetical (default): a "A ───" divider before each new first-letter
        // group, on every breakpoint. The divider spans the whole grid width so
        // each letter starts a fresh row. Collect the present letters for the
        // mobile A–Z jump rail.
        const letters = [];
        let current = null;
        list.forEach(i => {
          const letter = this.firstLetter(i.name);
          if (letter !== current) {
            current = letter;
            letters.push(letter);
            this.gridEl.appendChild(this.renderLetterDivider(letter));
          }
          this.gridEl.appendChild(this.renderCard(i));
        });
        this.renderAzIndex(letters);
      } else {
        // Most Popular: flat popularity_rank order, no letter grouping / rail.
        // (Iter-20: selected cards stay in position; selection never reorders.)
        list.forEach(i => this.gridEl.appendChild(this.renderCard(i)));
        this.renderAzIndex([]);
      }
    }

    // "Selected ingredients" section at the top of the grid: a full-width
    // header + a duplicate card for every stack item, in stack order
    // (newest first, same as the cart). Deliberately NOT the letter-divider
    // class — the mobile A–Z rail maps .yt-byo-letter-divider nodes to
    // letters, and this header must stay out of that map.
    renderSelectedSection() {
      if (!this.stack.length) return;
      // Founder 2026-08-07: while SEARCHING the section is suppressed —
      // "they do not need a list of 10 ingredients before they get their
      // result". The grid shows matches only; clearing the search brings
      // the Selected section straight back.
      if (this.filters.search) return;
      const head = document.createElement('div');
      head.className = 'yt-byo-selected-head';
      const label = document.createElement('span');
      label.className = 'yt-byo-selected-head__label';
      // Founder 2026-08-07: just "Selected", bigger (see CSS +25%).
      label.textContent = 'Selected';
      const rule = document.createElement('span');
      rule.className = 'yt-byo-selected-head__rule';
      rule.setAttribute('aria-hidden', 'true');
      head.appendChild(label);
      head.appendChild(rule);
      this.gridEl.appendChild(head);
      this.stack.forEach(s => {
        const ing = this.catalogueById.get(s.ingredient_id);
        if (!ing) return;
        const dup = this.renderCard(ing);
        // Mark the duplicate so the toggle scroll-anchor targets the MAIN
        // grid card, never this copy (founder 2026-07-25 anti-jump fix).
        dup.setAttribute('data-selected-dup', '1');
        this.gridEl.appendChild(dup);
      });
      // Closing boundary (founder 2026-08-07): under the Most Popular sort
      // there are no letter dividers, so the selected duplicates flowed
      // straight into the rest of the grid with nothing marking where the
      // section ends. A solid dark full-width line closes the section on
      // EVERY sort. Same wrapper+span structure as the head — a bare
      // childless <div> in this grid gets display:none'd by a theme rule,
      // so the line lives on an inner span exactly like the head's rule.
      const end = document.createElement('div');
      end.className = 'yt-byo-selected-end';
      end.setAttribute('aria-hidden', 'true');
      const endRule = document.createElement('span');
      endRule.className = 'yt-byo-selected-end__rule';
      end.appendChild(endRule);
      this.gridEl.appendChild(end);
    }

    // Full-width "A ─────" group header inside the grid (grid-column: 1 / -1).
    renderLetterDivider(letter) {
      const div = document.createElement('div');
      div.className = 'yt-byo-letter-divider';
      div.setAttribute('data-letter', letter);
      div.id = 'yt-byo-letter-' + letter;
      const tag = document.createElement('span');
      tag.className = 'yt-byo-letter-divider__letter';
      tag.textContent = letter;
      const rule = document.createElement('span');
      rule.className = 'yt-byo-letter-divider__rule';
      rule.setAttribute('aria-hidden', 'true');
      div.appendChild(tag);
      div.appendChild(rule);
      return div;
    }

    // Mobile-only A–Z jump rail (right edge). A thin scroll TRACK with a THUMB
    // on the left, the bolded alphabet stretched out down the right. `letters`
    // is the ordered list of present first-letters; empty hides the rail
    // (desktop / popular sort / no results). The thumb tracks page scroll and
    // lines up with the letter currently in view (see updateAzThumb).
    renderAzIndex(letters) {
      if (!this.azEl) return;
      this.azEl.innerHTML = '';
      this.azThumb = null;
      if (!letters || letters.length === 0) {
        this.azEl.hidden = true;
        return;
      }
      this.azEl.hidden = false;

      const track = document.createElement('div');
      track.className = 'yt-byo-az__track';
      const thumb = document.createElement('div');
      thumb.className = 'yt-byo-az__thumb';
      track.appendChild(thumb);
      this.azThumb = thumb;

      const lettersWrap = document.createElement('div');
      lettersWrap.className = 'yt-byo-az__letters';
      letters.forEach(letter => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'yt-byo-az__letter';
        btn.textContent = letter;
        btn.setAttribute('data-letter', letter);
        btn.setAttribute('aria-label', 'Jump to ' + letter);
        btn.addEventListener('click', () => this.jumpToLetter(letter));
        lettersWrap.appendChild(btn);
      });

      this.azEl.appendChild(track);
      this.azEl.appendChild(lettersWrap);
      this.azLettersWrap = lettersWrap;
      requestAnimationFrame(() => this.updateAzThumb());
      // Re-run once layout below the grid has settled (footer/images change the
      // page height, which moves the whole-page scroll map).
      setTimeout(() => this.updateAzThumb(), 450);
    }

    // The letter button whose vertical centre is nearest a viewport Y (for
    // scrubbing the rail like a scrollbar).
    letterButtonNearestY(y) {
      if (!this.azEl) return null;
      const btns = this.azEl.querySelectorAll('.yt-byo-az__letter');
      let best = null;
      let bestDist = Infinity;
      btns.forEach(b => {
        const r = b.getBoundingClientRect();
        const d = Math.abs((r.top + r.bottom) / 2 - y);
        if (d < bestDist) { bestDist = d; best = b; }
      });
      return best;
    }

    // Place the letters AND the thumb against the same map: the whole-page
    // scroll, from the A group reaching the reading line (rangeStart, top of the
    // rail) to the very bottom of the page (rangeEnd = max scroll). Because
    // rangeEnd is the page bottom, the assets BELOW the grid (routine / chat-cta
    // / footer) take up the lower part of the range, which squishes Z up from
    // the bottom; A stays anchored at the top. Recomputed every scroll so it
    // can't go stale. The two share the map, so the thumb sits on the right
    // letter the whole way down.
    updateAzThumb() {
      if (!this.azEl || this.azEl.hidden || !this.azThumb || !this.gridEl || !this.azLettersWrap) return;
      const railH = this.azLettersWrap.getBoundingClientRect().height;
      const dividers = this.gridEl.querySelectorAll('.yt-byo-letter-divider');
      if (railH <= 0 || !dividers.length) return;

      const readingLine = 110; // px below the viewport top (clears the header)
      const docTop = (el) => el.getBoundingClientRect().top + window.scrollY;
      const rangeStart = docTop(dividers[0]) - readingLine;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const span = Math.max(1, maxScroll - rangeStart);

      // Short thumb (proportional but capped) so its centre is a precise pointer
      // and barely offset from the top letter.
      const visFrac = window.innerHeight / Math.max(1, document.documentElement.scrollHeight);
      const thumbH = Math.max(18, Math.min(30, visFrac * railH));
      const travel = Math.max(1, railH - thumbH);

      // Letters + thumb on the SAME map and SAME travel, both anchored by their
      // TOP edge. A maps to fraction 0 → its top sits at the very top of the
      // rail (CSS top-anchors the glyph), and the thumb's top edge lands level
      // with whichever letter's group is at the reading line.
      // Stretch the TOP of the alphabet up by 2.5% of the rail, tapering to 0 at
      // the bottom (founder request) — the bottom letter stays put, the top moves
      // up, spacing opens out toward the top. The down-shift is applied in full
      // at the bottom (taper = f / maxF) and fades to nothing at A (f = 0).
      // Even distribution down the rail (founder 2026-06-17): big letters must
      // never overlap, regardless of how ingredients cluster by first letter.
      // Each present letter gets an equal slot from top to bottom. The thumb
      // below still tracks the REAL page scroll, so it stays an accurate
      // position indicator and the active letter highlights as you scroll.
      const btns = this.azLettersWrap.querySelectorAll('.yt-byo-az__letter');
      const n = btns.length;
      btns.forEach((btn, i) => {
        btn.style.top = (n > 1 ? (i / (n - 1)) * travel : 0) + 'px';
      });

      const p = Math.max(0, Math.min(1, (window.scrollY - rangeStart) / span));
      this.azThumb.style.height = thumbH + 'px';
      this.azThumb.style.top = (p * travel) + 'px';

      // Active letter = last group whose divider has passed the reading line.
      let active = dividers[0].getAttribute('data-letter');
      dividers.forEach(d => {
        if (d.getBoundingClientRect().top <= readingLine) active = d.getAttribute('data-letter');
      });
      btns.forEach(b => b.classList.toggle('yt-byo-az__letter--active', b.getAttribute('data-letter') === active));
    }

    // Scroll the grid to a letter's group header.
    jumpToLetter(letter) {
      if (window.ytTrack) ytTrack('byo_filter_selected', { filter: 'az:' + letter });
      const el = document.getElementById('yt-byo-letter-' + letter);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    renderCard(ing) {
      const added = this.stack.find(s => s.ingredient_id === ing.id);
      const dose = added ? added.amount_mg : Number(ing.default_dose_mg);

      const card = document.createElement('article');
      card.className = 'yt-byo-card' + (added ? ' yt-byo-card--added' : '');
      card.setAttribute('data-id', ing.id);

      const nameRow = document.createElement('div');
      nameRow.className = 'yt-byo-card__name-row';
      const name = document.createElement('span');
      name.className = 'yt-byo-card__name';
      // Full name — long names wrap to a second line (CSS clamps at 2).
      // The width differs mobile/desktop so wrapping is handled by the
      // layout, not a fixed character cap.
      name.textContent = ing.name;
      name.title = ing.name;
      // 2026-06-03: i-badge promoted from <span> to <button> so it
      // opens the ingredient info modal on click (was hover-only title).
      // stopPropagation prevents the click from also bubbling to the
      // card's whole-card toggle handler.
      const info = document.createElement('button');
      info.type = 'button';
      info.className = 'yt-byo-card__info';
      info.textContent = 'i';
      info.setAttribute('aria-label', ing.name + ' — details');
      info.addEventListener('click', (e) => {
        e.stopPropagation();
        this.openInfoModal(ing);
      });
      // iter-26: info badge sits LEFT of the name (was right of it).
      // Append order = display order inside a flex row.
      nameRow.appendChild(info);
      nameRow.appendChild(name);
      card.appendChild(nameRow);

      if (ing.tagline) {
        const tag = document.createElement('p');
        tag.className = 'yt-byo-card__tagline';
        tag.textContent = ing.tagline;
        card.appendChild(tag);
      }
      // "Default" line — always rendered on every card, showing the
      // ingredient's default dose in display units (founder 2026-06-17).
      // Replaces the old NRV line: a real NRV existed for only 37 of 211
      // ingredients, so most cards read "NRV —". Every ingredient HAS a
      // default dose, so this line is always populated. (Keeps the
      // .yt-byo-card__nrv class purely for its existing styling.)
      const def = document.createElement('span');
      def.className = 'yt-byo-card__nrv';
      // Founder 2026-08-14: "/day" rides small after the amount, like the
      // CTA's /mo.
      def.innerHTML = 'Default - ' + mgToDisplay(ing.default_dose_mg, ing.unit) + unitSuffix(ing.unit)
        + '<span class="yt-byo-card__perday"> /day</span>';
      card.appendChild(def);

      // Dose row — designer iter-11: ONLY present when the ingredient
      // is in the stack. Unselected cards show name + tagline + NRV
      // only; the bottom area gets a spacer (yt-byo-card__dose-placeholder)
      // so the card preserves the same height as a selected one. That
      // way a partial-row of selected+unselected stays in a perfect
      // grid (rows don't drop / wrap because the heights differ).
      if (added) {
        const doseRow = document.createElement('div');
        doseRow.className = 'yt-byo-card__dose-row';
        const doseLabel = document.createElement('span');
        doseLabel.className = 'yt-byo-card__dose-label';
        doseLabel.textContent = 'Dosage';
        doseRow.appendChild(doseLabel);
        // iter-25: build the dose controls (input + step buttons) from the
        // shared factory so the card and the sticky-cart row stay identical.
        doseRow.appendChild(this.buildDoseControls(ing, dose, {
          stepBtnClass: 'yt-byo-card__step',
          inputClass: 'yt-byo-card__dose-value yt-byo-card__dose-input',
          unitClass: 'yt-byo-card__dose-unit',
          controlsClass: 'yt-byo-card__dose-controls',
        }));
        card.appendChild(doseRow);
      }
      // Designer iter-13: NO placeholder for unselected cards. With the
      // two-height layout (unselected 100px, selected 200px), unselected
      // cards take their natural content height inside the 100px box —
      // no need to reserve dose-row space.

      // Toggle add/remove (top-right). Founder 2026-08-07: selected shows a
      // CROSS (the action it offers is remove), styled the same white
      // circle as unselected — not the old olive-filled tick.
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'yt-byo-card__toggle';
      if (added) {
        toggle.textContent = '×';
      } else {
        // Founder 2026-08-14: unselected shows an OVAL price chip —
        // "+£x.xx" = what this ingredient ADDS at its default dose, on
        // the page's 30-day-monthly basis (founder 2026-09-01: the site
        // leads with the 30-day price everywhere).
        const addsSub =
          Number(ing.price_fee || 0) +
          Number(ing.price_multiple || 0) * Number(ing.raw_cost_per_mg || 0) * Number(ing.default_dose_mg || 0);
        toggle.classList.add('yt-byo-card__toggle--price');
        toggle.innerHTML =
          '<span class="yt-byo-card__toggle-plus" aria-hidden="true">+</span>&pound;' +
          addsSub.toFixed(2);
      }
      toggle.setAttribute('aria-label', added ? 'Remove from blend' : 'Add to blend');
      toggle.addEventListener('click', (ev) => {
        ev.stopPropagation();
        this.toggleIngredient(ing);
      });
      card.appendChild(toggle);

      // Whole-card click toggles add/remove — not just the + button
      // (founder 2026-06-17). The info badge, the +/✓ toggle, and every
      // dose control (steppers + typed input) call stopPropagation, so
      // those keep their own behaviour; a click anywhere else on the card
      // (name, subtitle, Default line, body) selects/deselects it.
      card.addEventListener('click', () => this.toggleIngredient(ing));

      return card;
    }

    // ─── stack mutations ────────────────────────────────────────────

    toggleIngredient(ing) {
      const idx = this.stack.findIndex(s => s.ingredient_id === ing.id);
      const wasAdd = idx < 0;
      // B5 duplicate-add warning (founder 2026-08-27): adding an ingredient
      // that's ALREADY in the chosen base ("extra iron") pops a heads-up —
      // they can carry on (the add-on stacks ON TOP of the base dose) or
      // back out. One acknowledgement per ingredient per session.
      if (wasAdd && this.base && this.baseIngredientIds.has(ing.id) && !this.dupAcknowledged.has(ing.id)) {
        this.openDupModal(ing);
        return;
      }
      // Scroll anchor (founder 2026-07-25): adding an ingredient grows the
      // Selected section ABOVE the grid, so everything under it shifted down
      // and the card the customer just tapped jumped away. Anchor the
      // viewport to the tapped ingredient's MAIN-grid card (never the
      // Selected-section duplicate) across the re-render so it stays put.
      const mainCard = () =>
        this.gridEl.querySelector(
          '.yt-byo-card[data-id="' + ing.id + '"]:not([data-selected-dup])'
        );
      const anchorEl = mainCard();
      const anchorTop = anchorEl ? anchorEl.getBoundingClientRect().top : null;
      if (idx >= 0) {
        this.stack.splice(idx, 1);
        // iter-25c: removing the ingredient clears its over-soft-max
        // acknowledgement, so a re-add starts fresh at the default
        // dose with the warning still armed.
        this.overMaxAcknowledged.delete(ing.id);
      } else {
        if (this.stack.length >= MAX_STACK) return;
        // Designer iter-5: new ingredients appear at the TOP of the
        // cart list (was bottom). Prepend rather than push so the most
        // recent is first.
        this.stack.unshift({ ingredient_id: ing.id, amount_mg: Number(ing.default_dose_mg) });
      }
      if (window.ytTrack) {
        if (wasAdd) ytTrack('byo_ingredient_added', { ingredient: ing.name, dose: Number(ing.default_dose_mg) });
        else ytTrack('byo_ingredient_removed', { ingredient: ing.name });
      }
      this.renderGrid();
      this.renderCart();
      if (anchorTop != null) this.holdAnchor(mainCard, anchorTop);
      // On add, snap the cart list scroll to the TOP so the new row
      // (which now lives at index 0) is the visible one. On remove we
      // leave the scroll position alone.
      if (wasAdd && this.cartListEl) {
        requestAnimationFrame(() => {
          this.cartListEl.scrollTop = 0;
        });
      }
      this.persistDebounced();
    }

    /* Keep the card returned by `cardEl()` at viewport offset `anchorTop`
     * across the re-render AND the layout that settles just after it.
     *
     * Correcting once isn't enough (founder 2026-07-25: "when it is
     * unselected then it jumps up a bit"). Removing an ingredient shrinks
     * the Selected section, so the correction scrolls UP — which trips the
     * header's scroll-up reveal, which pins the ticker (position: fixed)
     * and takes its ~33px out of the document flow a frame later, dragging
     * the whole grid up with it. Adding scrolls DOWN, so the header hides
     * instead and nothing shifts — which is why only removal jumped.
     *
     * So re-measure over the next few frames and absorb whatever moved.
     * Any scroll we didn't cause means the customer took over: stop. */
    holdAnchor(cardEl, anchorTop) {
      let expectedScrollY = window.scrollY;
      const correct = () => {
        if (Math.abs(window.scrollY - expectedScrollY) > 1) return false;
        const el = cardEl();
        if (!el) return false;
        const delta = el.getBoundingClientRect().top - anchorTop;
        if (Math.abs(delta) >= 0.5) {
          window.scrollBy(0, delta);
          expectedScrollY = window.scrollY;
        }
        return true;
      };
      if (!correct()) return;
      requestAnimationFrame(() => {
        if (!correct()) return;
        requestAnimationFrame(correct);
        setTimeout(correct, 200);
      });
    }

    updateDose(ingredientId, amountMg, ing) {
      const item = this.stack.find(s => s.ingredient_id === ingredientId);
      if (item) {
        item.amount_mg = amountMg;
      } else {
        // Auto-add on dose change of un-added ingredient
        if (this.stack.length < MAX_STACK) {
          this.stack.push({ ingredient_id: ingredientId, amount_mg: amountMg });
        }
      }
      // Iter-19: re-render BOTH the grid and the cart so the dose
      // value is in sync everywhere. Previously only the local <span>
      // got the textContent update + the cart got re-rendered, but
      // the grid card stayed stale when the change originated from
      // the cart (and vice versa). Re-rendering both costs a few ms
      // and is the simplest way to guarantee a single source of truth.
      this.renderGrid();
      this.renderCart();
      this.persistDebounced();
    }

    // ─── cart rendering ─────────────────────────────────────────────

    renderCartHeader() {
      // The header is hidden in the Liquid; it carries the tin's fixed name
      // for anything that reads it (no customer naming since 28 Sep 2026).
      if (this.stackNameEl) this.stackNameEl.textContent = this.defaultTinName();
    }

    renderCart() {
      this.renderCartHeader();
      this.cartListEl.innerHTML = '';
      // B5: the base is the TOP ROW of the blend list (founder spec) —
      // one line, priced, linking to its own PDP, removable with ×. The
      // "change" link went with the pick-a-base step (28 Sep 2026).
      if (this.base) {
        const row = document.createElement('li');
        row.className = 'yt-byo-cart__row yt-byo-cart__row--base';
        row.style.cssText = 'border:1.5px solid #1c1c1c;border-radius:10px;padding:10px 12px;display:flex;align-items:center;justify-content:space-between;gap:10px;';
        row.innerHTML =
          '<span style="min-width:0;">' +
            '<a href="/products/base-' + encodeURIComponent(this.base.slug) + '" target="_blank" rel="noopener" style="font-weight:700;text-decoration:none;color:inherit;display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' + this.escapeHtml(this.base.name) + '</a>' +
            '<span style="font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:#555;">Base &middot; ' + this.base.n_ingredients + ' ingredients</span>' +
          '</span>' +
          '<span style="flex:none;display:flex;align-items:center;gap:8px;">' +
            // Founder 2026-09-03: the base's own price on its row, like every line.
            '<span class="yt-byo-cart-row__price">&pound;' + (Math.round(Number(this.base.prices.sub) * 100) / 100).toFixed(2) + '/mo</span>' +
            // Lucas, 24 Sep 2026: a base is added and removed like an
            // ingredient — one line, one control — never taken apart.
            '<button type="button" class="yt-byo-cart-row__remove" data-yt-byo-remove-base aria-label="Remove the ' + this.escapeHtml(this.base.name) + ' base" title="Remove base" style="border:0;background:none;padding:2px 4px;font-size:18px;line-height:1;cursor:pointer;color:#555;">&times;</button>' +
          '</span>';
        const removeBaseBtn = row.querySelector('[data-yt-byo-remove-base]');
        if (removeBaseBtn) removeBaseBtn.addEventListener('click', () => this.removeBase());
        this.cartListEl.appendChild(row);
      } else {
        // Founder, 28 Sep 2026: the manufacturing fee is IN the blend from
        // the first paint — a fixed line the customer sees before adding
        // anything, and the price above already includes it. A base
        // swallows the fee (B5/D25), so a based blend shows no fee line.
        this.cartListEl.appendChild(this.renderFeeRow());
      }
      if (this.stack.length === 0 && !this.base) {
        this.cartListEl.appendChild(this.cartEmptyEl);
        this.cartEmptyEl.hidden = false;
      } else {
        this.cartEmptyEl.hidden = true;
        this.stack.forEach(s => {
          const ing = this.catalogueById.get(s.ingredient_id);
          if (!ing) return;
          this.cartListEl.appendChild(this.renderCartRow(s, ing));
        });
      }

      // No-price model (founder 2026-07-09): the BYO page shows no price
      // anywhere — pricing lives on the formula product page. The price
      // element is gone from the Liquid; guard in case of stale builds.
      if (this.priceEl) this.priceEl.textContent = '';

      // PRICE-FIRST everywhere (founder 2026-08-27): the mobile bar and the
      // desktop panel header both lead with the live total.
      // Founder 2026-09-01: the composition line ("Base + N add-ons") is
      // gone from the sticky — price only, matching the funnel's other
      // stickies.
      {
        const pNow = this.computePrices();
        // Founder 2026-09-01: the 30-day monthly is the displayed price.
        const subNow = Math.round(pNow.raw_sub * 100) / 100;
        // Founder 2026-09-03: the per-day figure rides with the price.
        const perDayNow = (Math.round((subNow / 30) * 100) / 100).toFixed(2);
        if (this.summaryLineEl) {
          this.summaryLineEl.innerHTML =
            '<span style="font-family:var(--yt-font-heading,Oswald,sans-serif);font-weight:500;font-size:21px;display:block;line-height:1.1;">&pound;' +
            subNow.toFixed(2) + '<span style="font-size:12px;">/mo</span>' +
            '<span style="font-family:var(--yt-font-body,Quicksand,sans-serif);font-weight:600;font-size:11px;color:var(--yt-ink-muted,#8c8c8c);margin-left:6px;">&pound;' + perDayNow + ' a day</span></span>';
        }
        const totalEl = this.pickerSection.querySelector('[data-yt-byo-cart-total]');
        // Founder 2026-08-28: the bare price read plain — a small plan
        // qualifier sits under it (the shown price IS the 90-day monthly).
        if (totalEl) totalEl.innerHTML = '&pound;' + subNow.toFixed(2) + '<span style="font-size:13px;">/mo</span>' +
          '<span style="display:block;font-family:var(--yt-font-body,Quicksand,sans-serif);font-weight:600;font-size:11.5px;letter-spacing:.04em;text-transform:uppercase;color:var(--yt-ink-muted,#8c8c8c);margin-top:3px;">&pound;' + perDayNow + ' a day &middot; on the 30-day plan</span>';
        // Founder 2026-08-28: the composition line under the total is gone
        // (it also double-escaped the base name via textContent).
      }
      // Live pricing (founder 2026-07-25): the line under the name is now the
      // LIVE PRICE, not the ingredient count — closed mobile bar, open sheet
      // and desktop cart all show it so the formula-page price is never a
      // surprise. "£50/month minimum" until the equation clears the floor.
      // Founder 2026-08-27: no price breakdown line ("Base: £X + Add-ons:
      // £Y") anywhere — the sticky's single price is the whole story.
      // 28 Sep 2026: the closed mobile bar is the only price a phone
      // visitor sees (the panel does not open there), so it names the fee
      // the price includes. Hidden with a base — the base swallows it.
      if (this.summaryCountEl) {
        if (this.base) {
          this.summaryCountEl.innerHTML = '';
          this.summaryCountEl.hidden = true;
        } else {
          this.summaryCountEl.innerHTML = 'incl. &pound;' + this.feeGbp().toFixed(2) + ' manufacturing &amp; lab fee';
          this.summaryCountEl.hidden = false;
        }
      }
      if (this.livePriceEl) this.livePriceEl.innerHTML = '';

      // Cart-list nav arrows — show/hide based on overflow
      this.updateCartNavArrows();

      // CTA enabled state. Founder 2026-08-14: while the 90-day monthly
      // is under the £45 floor the CTA is the gate — greyed, unpressable,
      // reading "£45/mo minimum" until the stack clears it.
      // B5: a base counts as content — a base-only tin is submittable.
      const enough = this.base
        ? this.stack.length <= MAX_STACK
        : this.stack.length >= MIN_STACK && this.stack.length <= MAX_STACK;
      const ctaPrices = this.computePrices();
      // PORTAL MODE IS EXEMPT (founder 2026-08-18, live bug): an existing
      // subscriber is ALREADY paying the floored price, so gating their edit
      // on the £45 new-order minimum locked them out of self-service
      // entirely — every blend at/below the £50 monthly floor prices at
      // £43.75 on the 90-day basis, so those customers could not even
      // re-submit their current blend, let alone REMOVE an ingredient
      // (which only makes it cheaper). Reported case: sub 854194397.
      const underMin = !this.portalSubscriptionId && ctaPrices.bundle_at_floor;
      // Headline basis: the 30-day monthly (founder 2026-09-01 — the
      // 30-day price leads everywhere; the 90-day plan stays available
      // but is no longer the default framing).
      const ctaShown = Math.round(ctaPrices.raw_sub * 100) / 100;
      const hasAnyIngredient = this.stack.length > 0 || !!this.base;
      const ctaDisabled = !enough || this.busy || underMin;
      // /mo rides smaller than the price, PDP-style. text-transform: none
      // stops the CTA's uppercase turning it into "/MO". The whole label
      // lives in ONE wrapper span: the buttons are flex containers, and
      // bare text fragments become separate flex items — which trims the
      // spaces ("£45/mominimum") and centres the /mo instead of sitting
      // it on the price's baseline.
      const PERMO = '<span class="yt-byo-cta__permo">/mo</span>';
      [...this.viewFormulaBtns, this.mobileCtaBtn].forEach(b => {
        if (!b) return;
        if (!b.dataset.ytOrigLabel) b.dataset.ytOrigLabel = b.textContent.trim();
        // Stale page-cache guard (founder 2026-08-14): Shopify pins
        // per-device HTML snapshots for a while, and old ones still
        // render the pre-rename desktop label. Fresh HTML says COMPLETE;
        // coerce the rest so every viewer sees it now.
        if (/REVIEW/i.test(b.dataset.ytOrigLabel)) b.dataset.ytOrigLabel = 'COMPLETE';
        // Founder 2026-08-14: the minimum message shows whenever the
        // build is under £45/mo — INCLUDING the empty stack. Once over,
        // the price itself moves onto the button: "£X.XX/mo COMPLETE"
        // (no dash — founder rev 2).
        // Founder 2026-08-15: the under-minimum button is a LIGHTER grey
        // than the disabled-black fade.
        b.classList.toggle('yt-byo-cta--undermin', underMin);
        // Founder 2026-08-22: the floor reads per DAY (£45/mo ÷ 30 = £1.50).
        // Bundle world (founder 2026-08-26): label reads FORMULATE, the
        // price sits on its RIGHT, and neither shows until the first
        // ingredient is in. Legacy keeps the price-first COMPLETE form.
        // Founder 2026-08-27: the button says NEXT, always, with NO price —
        // the price lives on the sticky bar, not the CTA.
        const ctaLabel = BUNDLES_MODE ? 'NEXT' : b.dataset.ytOrigLabel;
        b.innerHTML = '<span class="yt-byo-cta__label">' + (underMin
          ? '&pound;1.50<span class="yt-byo-cta__permo">/day</span> minimum'
          : BUNDLES_MODE
            ? ctaLabel
            : '<span class="yt-byo-cta__price">&pound;' + ctaShown.toFixed(2) + '</span>' + PERMO + ' ' + b.dataset.ytOrigLabel) + '</span>';
        b.disabled = ctaDisabled || (BUNDLES_MODE && !hasAnyIngredient);
        const remaining = Math.max(0, MIN_STACK - this.stack.length);
        if (this.stack.length < MIN_STACK) {
          b.title = this.copy.minStackTooltip.replace('{remaining}', remaining);
        } else if (this.stack.length >= MAX_STACK) {
          b.title = this.copy.maxStackTooltip;
        } else {
          b.title = '';
        }
      });

      // Minimum-progress bar (founder 2026-08-14): solid black fill showing
      // how close the build is to the £45/mo floor. Starts part-full — the
      // manufacturing fee is already "in" — and hides once the floor
      // clears (the CTA flips to the live price at the same moment).
      // The £45 floor is defined on the 90-day-monthly basis (lockstep
      // with formulator pricing) even though the page displays 30-day.
      const raw90ForFloor = Math.round(ctaPrices.raw_sub * 0.875 * 100) / 100;
      const minPct = PREPAID_FLOOR > 0
        ? Math.max(0, Math.min(100, (raw90ForFloor / PREPAID_FLOOR) * 100))
        : 0;
      this.minProgressEls.forEach(el => { el.hidden = !underMin; });
      this.minProgressFills.forEach(f => { f.style.width = minPct.toFixed(1) + '%'; });
    }

    /** The manufacturing & lab fee on the 30-day monthly basis the page
     *  displays — the same PRICE_BASE computePrices adds to every scratch
     *  blend (£15 in the bundle world; served by the catalogue payload). */
    feeGbp() {
      return round2(PRICE_BASE);
    }

    /** The fixed fee line at the top of a scratch blend: one line, priced,
     *  no controls — it cannot be removed, so it offers nothing to press. */
    renderFeeRow() {
      const row = document.createElement('div');
      row.className = 'yt-byo-cart-row yt-byo-cart-row--fee';
      row.setAttribute('data-yt-byo-fee-row', '');
      const head = document.createElement('div');
      head.className = 'yt-byo-cart-row__head';
      const name = document.createElement('span');
      name.className = 'yt-byo-cart-row__name';
      name.textContent = 'Manufacturing & lab fee';
      const price = document.createElement('span');
      price.className = 'yt-byo-cart-row__price';
      price.textContent = '£' + this.feeGbp().toFixed(2) + '/mo';
      head.appendChild(name);
      head.appendChild(price);
      row.appendChild(head);
      const tag = document.createElement('p');
      tag.className = 'yt-byo-cart-row__tagline';
      tag.textContent = 'Blending, lab testing and your tin. Included in every blend.';
      row.appendChild(tag);
      return row;
    }

    renderCartRow(item, ing) {
      const row = document.createElement('div');
      row.className = 'yt-byo-cart-row';

      const head = document.createElement('div');
      head.className = 'yt-byo-cart-row__head';
      const name = document.createElement('span');
      name.className = 'yt-byo-cart-row__name';
      name.textContent = ing.name;
      // Founder 2026-09-03: every line shows its own monthly price, so the
      // blend reads as a shelf of reasonably priced things.
      const linePrice = document.createElement('span');
      linePrice.className = 'yt-byo-cart-row__price';
      if (ing.raw_cost_per_mg != null) {
        const lp = round2(Number(ing.price_fee || 0) + Number(ing.price_multiple || 0) * Number(ing.raw_cost_per_mg) * Number(item.amount_mg || 0));
        linePrice.textContent = '£' + lp.toFixed(2) + '/mo';
      }
      // Info icon — matches the grid card. Click opens the ingredient
      // info modal (same modal element fills with this row's data).
      // 2026-06-03: promoted from <span> to <button>; stopPropagation
      // so it doesn't also trigger any parent click handlers.
      const info = document.createElement('button');
      info.type = 'button';
      info.className = 'yt-byo-card__info';
      info.textContent = 'i';
      info.setAttribute('aria-label', ing.name + ' — details');
      info.addEventListener('click', (e) => {
        e.stopPropagation();
        this.openInfoModal(ing);
      });
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'yt-byo-cart-row__remove';
      remove.textContent = '×';
      remove.setAttribute('aria-label', 'Remove ' + ing.name);
      remove.addEventListener('click', () => this.toggleIngredient(ing));
      head.appendChild(name);
      head.appendChild(info);
      head.appendChild(linePrice);
      head.appendChild(remove);
      row.appendChild(head);

      if (ing.tagline) {
        const tag = document.createElement('p');
        tag.className = 'yt-byo-cart-row__tagline';
        tag.textContent = ing.tagline;
        row.appendChild(tag);
      }

      const doseRow = document.createElement('div');
      doseRow.className = 'yt-byo-cart-row__dose-row';
      const lbl = document.createElement('span');
      lbl.className = 'yt-byo-card__dose-label';
      lbl.textContent = 'Dosage';
      doseRow.appendChild(lbl);
      // iter-25: shared dose-controls factory — input + step buttons.
      doseRow.appendChild(this.buildDoseControls(ing, item.amount_mg, {
        stepBtnClass: 'yt-byo-card__step',
        inputClass: 'yt-byo-card__dose-value yt-byo-card__dose-input',
        unitClass: 'yt-byo-card__dose-unit',
        controlsClass: 'yt-byo-card__dose-controls',
      }));
      row.appendChild(doseRow);

      return row;
    }

    // ─── cart-list nav arrows ───────────────────────────────────────

    /**
     * Compute the height of one cart row (uses the first row as the
     * sample; rows are equal-height because the content is consistent).
     * Returns null if the list is empty or unmounted.
     */
    cartRowHeight() {
      if (!this.cartListEl) return null;
      // An ingredient row, not the shorter fixed fee line at the top.
      const row = this.cartListEl.querySelector('.yt-byo-cart-row:not(.yt-byo-cart-row--fee)') ||
        this.cartListEl.querySelector('.yt-byo-cart-row');
      if (!row) return null;
      // Include the inter-row gap so a single click jumps past one
      // ENTIRE row including its spacer.
      const styles = window.getComputedStyle(this.cartListEl);
      const gap = parseFloat(styles.rowGap || styles.gap || '0') || 0;
      return row.getBoundingClientRect().height + gap;
    }

    scrollCartByRow(direction) {
      const stepPx = this.cartRowHeight();
      if (!stepPx || !this.cartListEl) return;
      this.cartListEl.scrollBy({
        top: stepPx * direction,
        behavior: 'smooth',
      });
    }

    /**
     * Designer iter-8 — arrows are PERMANENTLY visible. When there's
     * nowhere to scroll in a direction (list doesn't overflow, or we're
     * already at the top/bottom), the arrow goes to a disabled state
     * instead of being hidden. This way the customer always sees the
     * scroll affordance and the cart's vertical rhythm doesn't change
     * as ingredients are added/removed.
     */
    updateCartNavArrows() {
      const list = this.cartListEl;
      if (!list || !this.cartNavUp || !this.cartNavDown) return;
      this.cartNavUp.hidden = false;
      this.cartNavDown.hidden = false;
      const overflows = list.scrollHeight > list.clientHeight + 1;
      const atTop = list.scrollTop <= 1;
      const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
      this.cartNavUp.disabled = !overflows || atTop;
      this.cartNavDown.disabled = !overflows || atBottom;
    }

    // ─── pricing ────────────────────────────────────────────────────

    computePrices() {
      // Cost-plus model (2026-07-18, 63% margin — must match formulator pricing.ts):
      //   sub = BASE + Σ( fee + multiple × raw_cost_per_mg × amount_mg ),
      // floored — algebraically (M + £0.50×n + £11) ÷ 0.37: fee = £0.50 labour ÷
      // 0.37, multiple = 30 days ÷ 0.37, BASE = £11 fixed pool ÷ 0.37. Taper
      // is 0 (inert). One-time = sub × 1.25 (the 20%-off offer ladder).
      // B5 BASE (2026-08-27): with a base the model is ADDITIVE — the
      // founder-set base anchor plus each add-on at its component price.
      // No £15 fee pool (the base swallows it) and NO floors: the base
      // anchors the price, so a base-only £35 blend must not gate on the
      // £45 prepaid floor. Lockstep with formulator store.ts base pricing.
      let total = this.base ? 0 : PRICE_BASE;
      let priced = 0;
      this.stack.forEach(s => {
        const cat = this.catalogueById.get(s.ingredient_id);
        if (!cat || cat.raw_cost_per_mg == null) return;
        const rawOrdered = Number(cat.raw_cost_per_mg) * s.amount_mg;
        total += Number(cat.price_fee || 0) + Number(cat.price_multiple || 0) * rawOrdered;
        priced += 1;
      });
      total -= PRICE_TAPER_GBP * Math.max(0, priced - PRICE_TAPER_AFTER);
      if (this.base) {
        const baseSub = Number(this.base.prices.sub) || 0;
        const sub0 = round2(baseSub + total);
        const oneTime0 = round2(Number(this.base.prices.one_time) + round2(total * ONE_TIME_MULTIPLIER));
        return {
          variant: oneTime0,
          one_time: oneTime0,
          monthly_sub: sub0,
          three_month_bundle: round2(oneTime0 * BUNDLE_DISCOUNT),
          at_floor: false,
          raw_sub: sub0,
          bundle_at_floor: false,
        };
      }
      const sub = round2(Math.max(VARIANT_FLOOR, total));
      const rawSub = round2(Math.max(0, total));
      const oneTime = round2(sub * ONE_TIME_MULTIPLIER);
      // 90-day plan monthly: one-time × 0.7, floored at £45 — LOCKSTEP
      // with formulator pricing.ts three_month_bundle_price.
      const bundleRaw = round2(oneTime * BUNDLE_DISCOUNT);
      const bundle = Math.max(bundleRaw, PREPAID_FLOOR);
      return {
        variant: oneTime,
        one_time: oneTime,
        monthly_sub: sub,
        three_month_bundle: bundle,
        at_floor: total <= VARIANT_FLOOR,
        // Unfloored equation sub — the honest build-up the price line
        // shows while under the minimums (chips sum exactly into it).
        raw_sub: rawSub,
        // Drives the live line's "£45/month minimum" wording (the BYO
        // headline price is the 90-DAY monthly — founder 2026-08-13).
        bundle_at_floor: bundleRaw <= PREPAID_FLOOR,
      };
    }

    // ─── dose controls (iter-25 shared factory) ─────────────────────
    //
    // Builds the [-] [input] [unit] [+] cluster used by both ingredient
    // cards and sticky-cart rows. Returns a wrapper element.
    //
    // Behaviour:
    //   - The input is type="text" + inputmode="numeric" + pattern="[0-9]*"
    //     so mobile keyboards open the number pad without offering the
    //     iOS-style spinner buttons that fight our own +/- buttons.
    //     pattern keeps non-numeric paste safe-ish.
    //   - Typing commits on blur or Enter:
    //       * round DOWN to nearest step (anchored at min) — keeps the
    //         customer at-or-below their typed value, never above it
    //       * clamp at min on the low side (cannot go below min)
    //       * do NOT clamp at max; over-max IS allowed, but pops the
    //         warning modal every time the result exceeds max
    //   - The `-` button steps down by step, floored at min.
    //   - The `+` button steps up by step. If already at-or-above max,
    //     it does NOT add another step (we'd otherwise jump silently
    //     past max). Instead it pops the modal so the customer sees
    //     the warning and chooses SET TO MAX or CONTINUE.
    //   - The unit (mg / mcg / IU) is a static chip beside the input,
    //     unchanged from before — customer never types or edits the unit.
    buildDoseControls(ing, initialDose, classes) {
      const controls = document.createElement('div');
      controls.className = classes.controlsClass || '';

      const minus = document.createElement('button');
      minus.type = 'button';
      minus.className = classes.stepBtnClass || '';
      minus.textContent = '−';
      minus.setAttribute('aria-label', 'Decrease dose');

      const input = document.createElement('input');
      input.type = 'text';
      // inputmode + pattern — opens the numeric keypad on iOS / Android
      // without summoning the native spinner UI on desktop. type="text"
      // (not "number") because Safari injects a spinner on number inputs
      // that we don't want; and because we manage rounding ourselves.
      // Fractional grids (probiotics step in 0.5 billion CFU) need a decimal
      // keypad and a decimal point; integer grids keep the digits-only pad.
      const allowDecimals = /\./.test(mgToDisplay(ing.dose_step_mg, ing.unit))
        || /\./.test(mgToDisplay(ing.min_dose_mg, ing.unit));
      input.inputMode = allowDecimals ? 'decimal' : 'numeric';
      input.setAttribute('pattern', allowDecimals ? '[0-9]*[.,]?[0-9]*' : '[0-9]*');
      input.autocomplete = 'off';
      input.setAttribute('aria-label', ing.name + ' dose in ' + unitLabel(ing.unit));
      input.className = classes.inputClass || '';
      // The stack + catalogue are canonical mg; the input shows the dose in
      // the ingredient's listed display unit (mg/mcg/g/bn CFU). All the
      // min/max/step arithmetic below stays in the stored unit — we convert
      // only at this input boundary.
      input.value = mgToDisplay(initialDose, ing.unit);

      const unit = document.createElement('span');
      unit.className = classes.unitClass || '';
      // Founder 2026-08-14: dosage reads "Xmg/day" — /day small like the
      // CTA's /mo.
      unit.innerHTML = unitLabel(ing.unit) + '<span class="yt-byo-card__perday">/day</span>';
      unit.setAttribute('aria-hidden', 'true'); // already announced via input aria-label

      const plus = document.createElement('button');
      plus.type = 'button';
      plus.className = classes.stepBtnClass || '';
      plus.textContent = '+';
      plus.setAttribute('aria-label', 'Increase dose');

      const min = Number(ing.min_dose_mg);
      // iter-25: two ceilings.
      //   softMax (= max_dose_mg from the catalogue): the "we
      //     recommend not going above this" line. Warning modal pops
      //     every time the customer crosses it.
      //   hardMax (= hard_max_dose_mg ?? max_dose_mg): the absolute upper
      //   bound — the internal max; the sellable menu mints to it.
      //     Values are floored to hardMax (snapped down to step) — the
      //     customer cannot exceed it regardless of warning
      //     acknowledgement. 1000× is generous on purpose; we may
      //     tighten it per-ingredient later by adding a hard_max_mg
      //     column to the catalogue.
      const softMax = Number(ing.max_dose_mg);
      const hardMax = hardMaxOf(ing);
      const step = Number(ing.dose_step_mg);
      // Display↔mg conversion factor for this ingredient's listed unit.
      const f = unitFactor(ing.unit);
      const readMg = () => (Number(input.value) || 0) / f; // input is display units

      const updateBtnState = () => {
        const cur = readMg();
        minus.disabled = cur <= min;
        // iter-25: `+` disables only at the hard ceiling. Between
        // softMax and hardMax the customer can keep stepping and we'll
        // re-warn on each press.
        plus.disabled = cur >= hardMax;
      };

      // Commit a numeric value into state. Source: typed | stepped.
      // Typed values are rounded DOWN to step (anchored at min). Stepped
      // values are exact step arithmetic.
      // Clamp at the HARD ceiling — never write a value above hardMax,
      // regardless of CONTINUE acknowledgement, because once you're
      // there you're at the absolute limit.
      // `popModalIfOverSoftMax` triggers the warning modal when the
      // result crosses the softMax ceiling.
      const commit = (rawValue, opts = {}) => {
        const parsed = Number(rawValue);
        let next = isFinite(parsed) ? parsed : min;
        if (next < min) next = min;
        if (opts.snapToStep) {
          next = floorToStep(next, min, step);
          if (next < min) next = min;
        }
        // Hard ceiling: clamp + floor-to-step so we land on a valid value.
        if (next > hardMax) next = floorToStep(hardMax, min, step);
        // Reflect in the input (display units) regardless of whether it changed.
        input.value = mgToDisplay(next, ing.unit);
        // Analytics: capture the pre-commit dose so we only log REAL
        // changes (a blur with no edit stays silent). Dose is canonical mg.
        const prevItem = this.stack.find(s => s.ingredient_id === ing.id);
        const prevMg = prevItem ? prevItem.amount_mg : null;
        this.updateDose(ing.id, next, ing);
        if (next !== prevMg && window.ytTrack) {
          ytTrack('byo_dose_changed', { ingredient: ing.name, dose: next });
        }
        updateBtnState();
        if (
          opts.popModalIfOverSoftMax &&
          next > softMax &&
          !this.overMaxAcknowledged.has(ing.id)
        ) {
          // iter-25c: only pop the modal if this ingredient hasn't been
          // acknowledged yet for the current session-of-being-in-the-stack.
          this.openOverMaxModal(ing, next);
        }
      };

      // Prevent the card-level click (which expands ingredient info)
      // from firing when the customer interacts with the dose controls.
      const stop = (ev) => ev.stopPropagation();

      minus.addEventListener('click', (ev) => {
        stop(ev);
        const cur = readMg() || min;
        const next = Math.max(min, roundDose(cur - step));
        commit(next, { popModalIfOverSoftMax: false });
      });

      plus.addEventListener('click', (ev) => {
        stop(ev);
        const cur = readMg() || min;
        if (cur >= hardMax) {
          // At the absolute limit. Nothing to add.
          return;
        }
        const next = roundDose(cur + step);
        commit(next, { popModalIfOverSoftMax: true });
      });

      // Block the card-level click handler from firing on input focus
      input.addEventListener('click', stop);
      input.addEventListener('mousedown', stop);
      input.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter') {
          ev.preventDefault();
          input.blur(); // triggers the change handler below
        }
      });

      // Commit on blur and on the native 'change' event (which fires
      // on Enter for input[type=text] in most browsers; keydown above
      // explicitly blurs so even browsers that don't fire change still
      // commit). Both round-down to step.
      const onCommit = () => {
        // input.value is in display units — convert to mg for commit().
        // NB: the option key is popModalIfOverSoftMax (what commit() reads).
        // It was previously misspelled popModalIfOverMax here, so a TYPED
        // over-max dose silently skipped the safety warning that the +/-
        // buttons trigger — only stepping past max warned, typing past it did not.
        commit(readMg(), { snapToStep: true, popModalIfOverSoftMax: true });
      };
      input.addEventListener('blur', onCommit);
      input.addEventListener('change', onCommit);

      // Sanity strip non-digits on the fly without yet committing — keeps
      // the typed UI tidy without disrupting a partial entry.
      input.addEventListener('input', () => {
        // Allow digits only (plus ONE decimal point on fractional grids).
        // Strip the rest. Use replace() so the caret logic stays sane.
        const cleaned = allowDecimals
          ? input.value.replace(',', '.').replace(/[^\d.]/g, '').replace(/^([^.]*\.)(.*)$/, (m, head, tail) => head + tail.replace(/\./g, ''))
          : input.value.replace(/[^\d]/g, '');
        if (cleaned !== input.value) input.value = cleaned;
      });

      updateBtnState();

      controls.appendChild(minus);
      controls.appendChild(input);
      controls.appendChild(unit);
      controls.appendChild(plus);
      return controls;
    }

    // ─── over-max modal ─────────────────────────────────────────────
    //
    // iter-25: pops EVERY time the customer pushes the dose above max
    // (typed or via +). Buttons:
    //   - "SET TO MAX" (primary, data-yt-byo-overmax-back) snaps the
    //     dose down to max_dose_mg and re-renders.
    //   - "CONTINUE"   (secondary, data-yt-byo-overmax-continue) leaves
    //     the over-max value in place and just closes the modal.
    //
    // The theme settings still control the literal button labels (so
    // copy can be edited without code). The original setting key
    // `over_max_modal_go_back` is now read as "SET TO MAX" semantically;
    // we don't need to rename the setting — the section schema's default
    // copy is updated in the Liquid file so out-of-the-box installs
    // show the right label.

    openOverMaxModal(ing, currentValue) {
      // Guard: this is reached from a normal dose-change click. If the modal
      // shell is absent in a build, an unguarded deref throws inside the click
      // handler. The over-max warning is advisory (the dose is already clamped
      // to the hard ceiling in commit()), so failing open — skip the warning
      // rather than throw — is safe.
      if (!this.overMaxModal) return;
      if (window.ytTrack) ytTrack('byo_overmax_shown', { ingredient: ing.name });
      const detail = this.overMaxModal.querySelector('[data-yt-byo-overmax-detail]');
      if (detail) {
        detail.textContent = ing.name + ' max is ' + mgToDisplay(ing.max_dose_mg, ing.unit) + unitSuffix(ing.unit) + '. You\'re at ' + mgToDisplay(currentValue, ing.unit) + unitSuffix(ing.unit) + '.';
      }
      this.overMaxModal.hidden = false;
      const back = this.overMaxModal.querySelector('[data-yt-byo-overmax-back]');
      const cont = this.overMaxModal.querySelector('[data-yt-byo-overmax-continue]');
      const close = () => this.closeOverMaxModal();
      if (!back || !cont) return;
      // CONTINUE exists only when the internal max leaves headroom above the
      // customer max — otherwise the warning level IS the ceiling and the
      // only honest action is SET TO MAX (2026-08-27; pre-B5 any dose was
      // custom-mintable, so CONTINUE used to be always-on).
      cont.style.display = hardMaxOf(ing) > Number(ing.max_dose_mg) ? '' : 'none';
      // iter-25c: both buttons count as "I've now seen the warning for
      // this ingredient" — record the ack so subsequent dose changes
      // for this ingredient don't re-pop the modal. The ack clears
      // when the ingredient leaves the stack (see toggleIngredient).
      back.onclick = () => {
        this.overMaxAcknowledged.add(ing.id);
        if (window.ytTrack) ytTrack('byo_overmax_acknowledged', { ingredient: ing.name, action: 'set_to_max' });
        const max = Number(ing.max_dose_mg);
        this.updateDose(ing.id, max, ing);
        // iter-25: re-render both surfaces so the input field reflects
        // the new max value immediately (was only rendering the grid).
        this.renderGrid();
        this.renderCart();
        close();
      };
      cont.onclick = () => {
        this.overMaxAcknowledged.add(ing.id);
        if (window.ytTrack) ytTrack('byo_overmax_acknowledged', { ingredient: ing.name, action: 'continue' });
        close();
      };
    }
    closeOverMaxModal() { if (this.overMaxModal) this.overMaxModal.hidden = true; }

    /**
     * B5: "already in your base" modal — built lazily with the same modal
     * classes as the over-max dialog so it inherits the house styling.
     */
    openDupModal(ing) {
      if (!this.dupModal) {
        const m = document.createElement('div');
        m.className = 'yt-byo-modal';
        m.setAttribute('role', 'dialog');
        m.setAttribute('aria-modal', 'true');
        m.hidden = true;
        m.innerHTML =
          '<div class="yt-byo-modal__backdrop" data-yt-byo-dup-backdrop></div>' +
          '<div class="yt-byo-modal__inner">' +
            '<h2 class="yt-byo-modal__heading yt-display">Already in your base</h2>' +
            '<p class="yt-byo-modal__detail" data-yt-byo-dup-detail></p>' +
            '<div class="yt-byo-modal__actions">' +
              '<button type="button" class="yt-byo-modal__cta yt-byo-modal__cta--primary" data-yt-byo-dup-back>GO BACK</button>' +
              '<button type="button" class="yt-byo-modal__cta yt-byo-modal__cta--secondary" data-yt-byo-dup-continue>ADD ANYWAY</button>' +
            '</div>' +
          '</div>';
        (this.pickerSection || document.body).appendChild(m);
        this.dupModal = m;
      }
      const detail = this.dupModal.querySelector('[data-yt-byo-dup-detail]');
      const baseRow = (this.base.ingredients || []).find(i => {
        const cat = this.catalogueById.get(ing.id);
        return cat && i.slug === cat.slug;
      });
      if (detail) {
        detail.textContent =
          ing.name + ' is already in your ' + this.base.name + ' base' +
          (baseRow ? ' at ' + baseRow.dose_display + ' a day' : '') +
          '. Adding it here stacks MORE on top of that — carry on if that’s what you want.';
      }
      const close = () => { this.dupModal.hidden = true; };
      this.dupModal.querySelector('[data-yt-byo-dup-back]').onclick = close;
      this.dupModal.querySelector('[data-yt-byo-dup-backdrop]').onclick = close;
      this.dupModal.querySelector('[data-yt-byo-dup-continue]').onclick = () => {
        this.dupAcknowledged.add(ing.id);
        if (window.ytTrack) { try { ytTrack('byo_dup_base_acknowledged', { ingredient: ing.name }); } catch (_e) {} }
        close();
        this.toggleIngredient(ing);
      };
      this.dupModal.hidden = false;
    }

    // ─── submit ─────────────────────────────────────────────────────

    async handleSubmit(branch) {
      if (this.busy) return;
      // No naming step (founder, 28 Sep 2026): NEXT submits straight away.
      // Analytics: which CTA fired the submit. The single-CTA build only has
      // VIEW FORMULA ('formula'); 'basket' covers older builds where the
      // standalone ADD TO BASKET button still exists in the DOM.
      if (window.ytTrack) ytTrack(branch === 'basket' ? 'byo_add_to_basket' : 'byo_view_formula');
      // Portal "Change my stack" — any CTA means "update my subscription".
      if (this.portalSubscriptionId) branch = 'subscription';
      this.busy = true;
      // Null-guard: this runs BEFORE the try, so an unguarded deref on a missing
      // errorRegion would throw with busy already latched true → every CTA
      // permanently dead (the busy-flag deadlock class). Guard it.
      if (this.errorRegion) this.errorRegion.hidden = true;
      this.renderCart(); // disables CTAs via busy guard
      try {
        // ALWAYS-FRESH (2026-06-26): the old "no-op shortcut" (reuse the
        // existing variant when the stack hash was unchanged) is REMOVED. Every
        // press of ADD TO BASKET / VIEW FULL FORMULA now mints a brand-new
        // variant via submitFormula() → /formula/submit. We never reuse a
        // variant — a shared ?stack= URL must never let a second visitor's
        // click ride (or rewrite) the first customer's variant/subscription.

        // Use the dedicated submit loader, not the shared pearl loader.
        // submitFormula() now calls onProgress at 15 / 30 / 75% so the bar
        // advances on REAL waypoints (hash done / fetch sent / response in).
        // startSubmitDrift() fills the dead gap between 75% and the cart-add
        // resolution so the bar never sits still.
        // BUNDLE WORLD: no loader at all — submits return in ~1s (nothing
        // mints), so the step-1-2-3 choreography reads as fake (founder
        // 2026-08-26). CTAs stay disabled via the busy flag.
        if (!BUNDLES_MODE) this.showSubmitLoader();

        // Timeline mode: the loader runs its fixed choreography; the
        // waypoints no longer drive the bars (finishSubmitFill is the
        // one reality gate at the end).
        const result = await this.submitFormula(branch, () => {});

        // Analytics: formula minted successfully. Wrapped so a pricing
        // hiccup can never break the redirect chain below.
        if (window.ytTrack) {
          try {
            ytTrack('byo_submitted', {
              ingredient_count: this.stack.length,
              price: this.computePrices().variant,
            });
          } catch (_e) { /* analytics must never break submit */ }
        }

        // Shopify's ?variant=<id> URL param only resolves numeric variant
        // ids — if you pass the GraphQL GID (`gid://shopify/ProductVariant/…`)
        // Shopify silently falls back to product.selected_or_first_available_variant
        // which is the parent's DEFAULT variant (a different formula at a
        // different price). Strip non-digits before redirecting.
        const isBundle = Boolean(result.bundle);
        const numericVariantId = isBundle ? '' : String(result.variant_id).replace(/\D/g, '');

        if (branch === 'subscription') {
          // Re-point the existing subscription at the new variant (Recharge swap).
          // Does NOT add a cart line / create a second subscription.
          // Bundle world skips the submit loader for purchases, but the
          // subscription edit still wants its CONFIRMATION screen ("was
          // £X → £Y") — show it here for that one branch.
          if (BUNDLES_MODE) this.showSubmitLoader();
          this.setSubmitStep('Updating your subscription…');
          let swap;
          if (isBundle) {
            // Bundle sub: replace selection contents in place - no minting,
            // no variant swap, no sync lag. Price disclosure uses the
            // formula's computed monthly price (the charge re-prices from
            // the new selections).
            await this.updateBundleSubscription(this.portalSubscriptionId, result.formula_id);
            swap = {
              new: { price: result.prices && result.prices.monthly_sub != null ? Number(result.prices.monthly_sub).toFixed(2) : null },
              old: {},
            };
          } else {
            swap = await this.swapSubscription(
              this.portalSubscriptionId,
              numericVariantId,
              result.formula_id,
            );
          }
          this.finishSubmitFill('Subscription updated!');
          this.stopSubmitDrift();

          // Price-change disclosure (UK CRA §25) shown in the loader before we
          // send the customer back to their account (the Recharge portal lives
          // behind the "Subscriptions" link there).
          const newPrice = swap && swap.new && swap.new.price;
          const oldPrice = swap && swap.old && swap.old.price;
          let nextDate = '';
          if (swap && swap.next_charge_at) {
            const d = new Date(swap.next_charge_at);
            if (!isNaN(d.getTime())) {
              nextDate = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
            }
          }
          let msg = 'Your subscription now uses your new formula.';
          if (newPrice) {
            msg =
              'From your next delivery' + (nextDate ? ' on ' + nextDate : '') +
              ', you’ll pay £' + newPrice + '/month' +
              (oldPrice && oldPrice !== newPrice ? ' (was £' + oldPrice + ')' : '') + '.';
          }
          const headingEl = this.loadingSection.querySelector('[data-yt-byo-submit-heading]');
          const sublineEl = this.loadingSection.querySelector('[data-yt-byo-submit-subline]');
          if (headingEl) headingEl.textContent = 'Subscription updated';
          if (sublineEl) sublineEl.textContent = msg + ' Taking you back to your account…';

          setTimeout(() => window.location.assign('/account'), 4500);
          return;
        }

        if (isBundle) {
          // BUNDLE MODE (doc 05 step 4): hand the blend to the summary PDP
          // (the parent product's page) — review + plan choice + add live
          // there. Nothing touches the cart until the customer confirms.
          try {
            const ings = this.stack.map((it) => {
              const cat = this.catalogueById.get(it.ingredient_id);
              // mcg ingredients display ×1000 as µg (PDP convention);
              // bn_cfu probiotics show the number as billions of CFU.
              const cfu = cat && String(cat.unit || '').toLowerCase() === 'bn_cfu';
              const mcg = !cfu && cat && cat.unit === 'mcg';
              const doseNum = mcg ? Math.round(it.amount_mg * 1000) : it.amount_mg;
              // Cost-only share (sub basis): the PDP rescales these
              // proportionally to (selected price − fee), same as legacy.
              const share = cat && cat.raw_cost_per_mg != null
                ? Math.round((Number(cat.price_fee || 0) + Number(cat.price_multiple || 0) * Number(cat.raw_cost_per_mg) * it.amount_mg) * 100) / 100
                : 0;
              return {
                name: cat ? cat.name : it.ingredient_id,
                slug: cat ? cat.slug : null,
                dose_num: this.formatDose(doseNum),
                // The PDP prints dose_num + dose_unit with no separator.
                dose_unit: cfu ? ' bn CFU' : mcg ? 'µg' : 'mg',
                share: share,
              };
            });
            const pr = this.computePrices();
            sessionStorage.setItem('yt_blend_summary', JSON.stringify({
              name: this.defaultTinName(),
              mode: this.purchaseMode || 'sub',
              // Server truth when present (base pricing is additive there);
              // the client equation is the fallback for older responses.
              prices: {
                sub: (result.prices && result.prices.monthly_sub != null) ? Number(result.prices.monthly_sub) : pr.raw_sub,
                one_time: (result.prices && result.prices.one_time != null) ? Number(result.prices.one_time) : Math.round(pr.raw_sub * ONE_TIME_MULTIPLIER * 100) / 100,
              },
              // B5: no fee line with a base — the base price swallows it.
              fee: this.base ? 0 : PRICE_BASE,
              base: this.base ? {
                slug: this.base.slug,
                name: this.base.name,
                n_ingredients: this.base.n_ingredients,
                prices: this.base.prices,
              } : null,
              ingredients: ings,
              bundle: result.bundle,
              spoon_variant_id: this.spoonVariantId || null,
              formula_id: result.formula_id || null,
              at: Date.now(),
            }));
          } catch (_e) { /* storage full/blocked → fall through to basket */ }
          // ?blend=<id> makes the PDP URL shareable + refresh-proof (the
          // PDP hydrates from the formulator when storage is missing).
          // Founder 2026-08-28: BASE blends land on the base's LANDING page
          // (the designed PDP) with their add-ons preloaded via ?blend=;
          // base-less blends keep the personalised-blend PDP.
          if (this.base && this.base.slug && result.formula_id) {
            window.location.assign('/products/base-' + encodeURIComponent(this.base.slug) +
              '?blend=' + encodeURIComponent(result.formula_id));
            return;
          }
          window.location.assign('/products/personalised-blend' +
            (result.formula_id ? '?blend=' + encodeURIComponent(result.formula_id) : ''));
          return;
        }

        if (branch === 'basket') {
          // Step 3 (Making) is already active; the cart-add IS the making.
          await this.addVariantToShopifyCart(numericVariantId);
          this.finishSubmitFill('All set — opening your basket…');
        } else {
          // Step 3 (Making) completes when the variant is GENUINELY buyable:
          // poll the product JSON for the new variant id. Replaces the fixed
          // 15s index-lag soak (founder 2026-08-13 — "smart, variable"). A
          // poll timeout falls through on purpose: the PDP's own not-ready
          // handling backstops the rare >25s Shopify index laggard.
          // Capped at 12s (founder 2026-08-13: long end-of-step waits
          // read as stalls; the PDP backstop is the better trade past ~12s).
          await (window.ytPollVariantReady
            ? ytPollVariantReady('custom-blend', numericVariantId, { timeoutMs: 12000, gapMs: 2000 })
            : new Promise(function (resolve) { setTimeout(resolve, 8000); }));
          this.finishSubmitFill('All set — opening your formula…');
        }
        this.stopSubmitDrift();

        // The loader STAYS UP through the navigation (founder 2026-07-28).
        // We used to resetLoaderToPicker() here so the bfcache wouldn't
        // snapshot the submitting state — but window.location.assign() only
        // STARTS a navigation; the current page keeps rendering until the
        // next page's response paints. On a slow connection that meant the
        // picker flashed back for seconds after "All set", which read as a
        // failed submit right at the moment of maximum commitment. And the
        // reset never reliably fixed bfcache anyway — Safari snapshots
        // before late mutations, which is exactly why the pageshow
        // (e.persisted) listener at the bottom of this file exists. That
        // listener is the real bfcache fix: it resets the loader (and
        // this.busy) on Back-restore in every engine, so nothing here needs
        // to pre-empt it. The subscription branch above has always worked
        // this way — loader up until the browser paints /account.
        //
        // iter-24: still NOT calling store.clear() — the customer's stack
        // survives a Back to BYO so they can tweak and re-submit (every
        // re-submit mints a fresh variant — always-fresh).

        const target = branch === 'basket'
          ? '/cart'
          : '/products/custom-blend?variant=' + encodeURIComponent(numericVariantId);
        // 1.4s lets the finish glide (90 → 100) visibly complete before
        // the navigation — the founder's "slowly goes to the end to make
        // sure it all worked before sending them on".
        setTimeout(() => window.location.assign(target), 1400);
      } catch (e) {
        this.stopSubmitDrift();
        this.busy = false;
        this.showPicker();
        // The validator's field paths are for developers; the customer gets
        // the sentence ("300 mg is not a dose we make for …"). This used to
        // live on the naming screen (Pete Davies, 24 Sep 2026); with that
        // screen gone it is said here.
        const msg = ((e && e.message) || this.copy.networkError)
          .replace(/^-?\s*ingredients\[\d+\]\.amount_mg:\s*/gm, '')
          .replace(/(\d+) not on the dose grid for ([a-z0-9-]+) \(([^)]*)\)/g, function (_m, mg, slug, grid) {
            return mg + ' mg is not a dose we make for ' + slug.replace(/-/g, ' ') + ' (' + grid + ' mg)';
          })
          .replace(/^\s*[-•]\s*/gm, '')
          .replace(/\n+/g, ' · ')
          .replace(/^\w/, function (c) { return c.toUpperCase(); });
        if (window.ytTrack) { try { ytTrack('byo_submit_error', { message: msg.slice(0, 120) }); } catch (_e) {} }
        if (this.errorRegion) {
          this.errorRegion.hidden = false;
          this.errorRegion.textContent = msg;
        }
        // The error region sits in the cart panel, which a phone never
        // opens — and a failure after the loader closes must never be
        // silent (that is exactly how we lose them). Toast it when the
        // region is missing or not on screen.
        if (!this.errorRegion || this.errorRegion.offsetParent === null) {
          this.showToast(msg, 9000);
        }
      }
    }

    /**
     * Forcibly returns the page to picker-visible state. Used after a
     * successful submit + cart-add but BEFORE the navigation flushes, so
     * the bfcache snapshot is the picker, not the loader. Without this,
     * customers hitting Back from /cart or the Formula PDP land on a
     * frozen "Making your formula" panel.
     */
    resetLoaderToPicker() {
      this.busy = false;
      if (!this.loadingSection || !this.pickerSection) return;
      this.loadingSection.hidden = true;
      this.loadingSection.dataset.state = 'loading';
      const errEl = this.loadingSection.querySelector('[data-state-error]');
      const loadEl = this.loadingSection.querySelector('[data-state-loading]');
      const subEl = this.loadingSection.querySelector('[data-state-submitting]');
      if (errEl) errEl.hidden = true;
      if (loadEl) loadEl.hidden = false;
      if (subEl) subEl.hidden = true;
      // Reset the bar so a future submit starts from 0, not 100. Kill the
      // transition first so it SNAPS to 0 instead of animating backward
      // (the bar still carries the long glide / quick-finish transition).
      const bar = this.loadingSection.querySelector('[data-yt-byo-progress-bar]');
      if (bar) { bar.style.transition = 'none'; bar.style.width = '0%'; }
      this.pickerSection.hidden = false;
      // iter-24: re-render the cart so CTAs recompute their disabled
      // state from current stack.length + busy. Without this, on
      // bfcache restore the buttons keep whatever disabled state was
      // snapshotted at submit time — visually "no ingredients" even
      // when the stack is full of ingredients and busy is now false.
      this.renderCart();
    }

    /**
     * Single console.warn when the 1-month Recharge selling-plan id is
     * blank. Ported from yt-formula-pdp.js warnIfMissingPlans(). BYO only
     * ships the 1-month plan as its default (the 3-month bundle is chosen
     * later on the PDP/cart), so we only check the 1-month id here.
     * Purely diagnostic — it never blocks ADD TO BASKET.
     */
    warnIfMissingPlan() {
      if (!this.sellingPlan1Mo) {
        console.warn(
          '[yt-byo] No Recharge selling-plan ID for the 1-month subscription — ADD TO BASKET will fall back to a one-time purchase. Set the ID in the section settings (Recharge 1-month selling plan ID).'
        );
      }
    }

    async addVariantToShopifyCart(variantId) {
      const numericId = String(variantId).replace(/\D/g, '');
      // Default plan = 1-month subscription (per docs/07-decisions/0005-recharge-default-sub.md).
      // When sellingPlan1Mo is set (plan 690375786821), the line subscribes
      // at the SAME price as one-time — the plan carries empty/0% pricing, so
      // there's no subscriber discount. The subscription value is perks-only:
      // free shipping + free weighing spoons (the spoon + shipping Function
      // zeroes them when the cart holds a subscription line). When empty
      // (Recharge not yet configured), we omit the field and Shopify creates
      // a plain one-time line at the variant's raw `variant_price` — which
      // surfaces the "Recharge isn't wired" issue rather than hiding it.
      const item = { id: numericId, quantity: 1 };
      // Per-order ingredient list as a line-item property — it rides onto the
      // Shopify order, so Klaviyo's native order sync can show the customer's
      // actual blend in emails (not just the parent "Custom Formula" product).
      const ingredientsText = (this.stack || [])
        .map((s) => {
          const ing = this.catalogueById.get(s.ingredient_id);
          const u = ing ? ing.unit : 'mg';
          return ((ing && ing.name) || 'Ingredient') + ' — ' + mgToDisplay(s.amount_mg, u) + unitSuffix(u);
        })
        .join(' · ')
        .slice(0, 500);
      if (ingredientsText) item.properties = { Ingredients: ingredientsText };
      if (this.sellingPlan1Mo) item.selling_plan = this.sellingPlan1Mo;
      // Auto-add the weighing spoons as a plain one-time line (NO selling plan, so
      // it never recurs — naturally first-order-only). Added to every cart; a
      // Shopify discount Function zeroes it (and shipping) when the cart holds a
      // subscription line. Customers can remove it in the cart.
      const items = [item];
      // Spoon rides subscription carts only (the shipping Function zeroes
      // it there); one per cart (dedupe), none in one-time mode.
      if (await this.spoonLineNeeded()) items.push({ id: this.spoonVariantId, quantity: 1 });
      const body = { items: items };

      // iter-23: retry on 422. 2026-07-19: budget extended ~10s → ~60s.
      //
      // Newly-created variants take ~5-60s to appear in Shopify's
      // storefront cart index after productVariantsBulkCreate, even
      // though publishablePublish runs on the server. The variant
      // shows up in /products/<handle>.js immediately, but
      // /cart/add.js returns 422 with "already sold out" (Shopify's
      // misleading default message for "variant exists but isn't in
      // the cart-eligible index yet") until the index catches up.
      //
      // The old 3-retry / ~9.5s budget still lost the 10-60s tail —
      // a customer who clicked straight through the loader hit
      // "something went wrong" even though their variant was fine
      // (founder 2026-07-19). Now: keep retrying on 422 across a
      // ~60s ladder, holding the customer on the loader with
      // patient copy instead of erroring — a fast index costs the
      // customer nothing extra, a slow one just looks like a slow
      // connection and then works.
      //
      // We don't retry on non-422 errors (those mean something else
      // is wrong) and we never retry past 422 — once cart-add
      // succeeds, we move on.
      const delays = [0, 1500, 3000, 5000, 6500, 8000, 10000, 12000, 14000];
      let lastText = '';
      for (let i = 0; i < delays.length; i++) {
        if (delays[i] > 0) {
          // Surface a progress hint to the customer mid-retry so the
          // loader doesn't sit on the same step text. Label only — the bar
          // width is the smooth glide (startSubmitDrift), and setting a
          // width here would fight that animation. From the 4th attempt
          // the copy shifts to the patient "slow connection" register.
          this.setSubmitStep(
            i < 4
              ? 'Finalising in our system…'
              : 'Still working — this is taking a little longer than usual…'
          );
          await new Promise((resolve) => setTimeout(resolve, delays[i]));
        }
        const res = await fetchWithTimeout('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }, 15000, 'Adding to your basket is taking longer than expected. Please try again.');
        if (res.ok) return;
        lastText = await res.text();
        // Password-page interception. While the Online Store is in
        // pre-launch (password-protected), /cart/add.js returns the
        // password page HTML instead of JSON whenever the visitor's
        // `storefront_digest` cookie is missing or expired. Detect it
        // (HTML body, 401/403, or a redirect to /password) and surface a
        // clear instruction rather than dumping raw HTML at the customer.
        // Once the store password is removed at launch this never fires.
        var looksLikePasswordPage =
          res.status === 401 ||
          res.status === 403 ||
          (res.url && res.url.indexOf('/password') !== -1) ||
          /^\s*<(!doctype|html)/i.test(lastText);
        if (looksLikePasswordPage) {
          throw new Error(
            'The store is password-protected (pre-launch). Re-enter the store password, then try again.'
          );
        }
        // Not a 422 → permanent failure, stop retrying.
        if (res.status !== 422) {
          throw new Error('Could not add to cart. ' + lastText.slice(0, 120));
        }
      }
      throw new Error('Could not add to cart. ' + lastText.slice(0, 120));
    }

    /**
     * The tin name — the ONLY name a blend gets (founder, 28 Sep 2026: "we
     * will not be allowing people to choose the name of their tin
     * anymore"). A base's short tin_name ("My Platinum", "Daily Greens")
     * when the blend is built on one, else "My Blend". LOCKSTEP with the
     * server rule in formulator src/formula/name.ts (customerTinName),
     * which overrides whatever a customer submit sends.
     */
    defaultTinName() {
      if (!this.base) return 'My Blend';
      const tin = this.base.tin_name;
      return tin ? String(tin).trim().slice(0, TIN_NAME_MAX) || 'My Blend' : 'My Blend';
    }

    // ─── full render ────────────────────────────────────────────────

    renderAll() {
      this.renderFilters();
      this.renderGrid();
      this.renderCart();
    }
  }

  let controllerInstance = null;

  function init() {
    const root = document.querySelector('[data-yt-byo-picker]');
    if (!root) return;
    controllerInstance = new BYOController(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // bfcache reset — the ONLY loader reset on the success path.
  //
  // Browsers restore the entire page DOM from the back/forward cache when
  // the user navigates back, INCLUDING the 'submitting' state of the
  // loader section. handleSubmit() deliberately does NOT reset the loader
  // before window.location.assign() (2026-07-28) — the assign only STARTS
  // a navigation, so a pre-nav reset flashed the picker for seconds on
  // slow connections and read as a failed submit. Instead the loader stays
  // up until the next page paints, and THIS listener restores the picker
  // (and clears this.busy) when the page comes back via bfcache —
  // event.persisted is the restore signal in every engine, including
  // Safari, whose early DOM snapshot is why a pre-nav reset never worked
  // there anyway. A non-bfcache Back reloads from network and boots fresh,
  // so every return path lands on the picker.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted || !controllerInstance) return;
    controllerInstance.resetLoaderToPicker();
  });
})();
